import React, { useEffect, useId, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  GitCompare,
  Lightbulb,
  Loader2,
  Mail,
  Maximize2,
  MessageSquare,
  Minimize2,
  Plus,
  RefreshCw,
  Send,
  Sparkles,
  UploadCloud,
  User,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import MarkdownChatContent from '../components/MarkdownChatContent';
import { formatNdaClauseLines, isNdaEnumeratedLine } from './ndaClauseText';

const DEFAULT_LIMIT = 5;
const TYPEWRITER_INTERVAL_MS = 18;
const CONTRACT_QA_API_BASE = (process.env.REACT_APP_CONTRACT_QA_API_BASE || '/api/contract-qa').replace(/\/$/, '');

const AGREEMENT_TYPE_GROUPS = [
  {
    label: 'Construction Agreements',
    options: [
      ['prime_contract', 'Prime Contract / Owner-Contractor Agreement'],
      ['subcontract', 'Subcontract'],
      ['master_agreement', 'Master Services / Master Subcontract Agreement'],
      ['change_order', 'Change Order / Amendment / Addendum'],
      ['letter_of_intent', 'Letter of Intent / Preconstruction Agreement'],
      ['site_access', 'Site Access / License / Easement Agreement'],
    ],
  },
  {
    label: 'Commercial Agreements',
    options: [
      ['purchase_order', 'Purchase Order'],
      ['supply_vendor', 'Supply / Vendor Agreement'],
      ['professional_services', 'Professional Services / Consultant Agreement'],
      ['equipment_rental', 'Equipment Rental / Lease Agreement'],
    ],
  },
  {
    label: 'Corporate Agreements',
    options: [
      ['joint_venture_teaming', 'Joint Venture / Teaming Agreement'],
      ['employment_independent_contractor', 'Employment / Independent Contractor Agreement'],
      ['guaranty_bond', 'Guaranty / Bond / Indemnity Agreement'],
    ],
  },
  { label: 'Other', options: [['other', 'Other']] },
];

const agreementTypeLabel = (value) => {
  for (const group of AGREEMENT_TYPE_GROUPS) {
    const match = group.options.find(([key]) => key === value);
    if (match) return match[1];
  }
  return value || 'Agreement';
};

export const NDA_INFORMATION_FLOW_OPTIONS = [
  {
    value: 'sylvan_discloses',
    shortLabel: 'Sylvan discloses',
    description: 'The counterparty receives Sylvan information.',
  },
  {
    value: 'counterparty_discloses',
    shortLabel: 'Sylvan receives',
    description: 'The counterparty discloses to Sylvan.',
  },
  {
    value: 'mutual',
    shortLabel: 'Mutual',
    description: 'Both parties disclose and receive information.',
  },
];

export const ndaInformationFlowLabel = (value) => (
  NDA_INFORMATION_FLOW_OPTIONS.find((option) => option.value === value)?.shortLabel || value
);

const EXAMPLE_PROMPTS = [
  'Can you give me examples of indemnity clauses?',
  'Show me payment clause examples.',
  'Find clauses like this one: Contractor shall defend, indemnify, and hold harmless Owner.',
];

const ContractQAAgent = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      text: 'Ask naturally. I can tell whether you want clause-type examples or clauses similar to language you paste.',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [contractAgreementType, setContractAgreementType] = useState('');
  const [contractAgreementTypeDetail, setContractAgreementTypeDetail] = useState('');
  const [contractJob, setContractJob] = useState(null);
  const [contractFindings, setContractFindings] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [comparisonBaseFile, setComparisonBaseFile] = useState(null);
  const [comparisonRevisedFile, setComparisonRevisedFile] = useState(null);
  const [comparisonAgreementType, setComparisonAgreementType] = useState('');
  const [comparisonAgreementTypeDetail, setComparisonAgreementTypeDetail] = useState('');
  const [comparisonJob, setComparisonJob] = useState(null);
  const [comparisonSummary, setComparisonSummary] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [uploadModalMode, setUploadModalMode] = useState(null);
  const [assistantExpanded, setAssistantExpanded] = useState(false);
  const [compactPane, setCompactPane] = useState('chat');
  const messagesEndRef = useRef(null);
  const activeUploadRunRef = useRef(0);

  useEffect(() => {
    const wideLayout = window.matchMedia?.('(min-width: 1400px)');
    if (!wideLayout) return undefined;
    const handleLayoutChange = (event) => {
      if (!event.matches) setAssistantExpanded(false);
    };
    handleLayoutChange(wideLayout);
    if (wideLayout.addEventListener) {
      wideLayout.addEventListener('change', handleLayoutChange);
      return () => wideLayout.removeEventListener('change', handleLayoutChange);
    }
    wideLayout.addListener?.(handleLayoutChange);
    return () => wideLayout.removeListener?.(handleLayoutChange);
  }, []);

  const startUploadRun = () => {
    activeUploadRunRef.current += 1;
    return activeUploadRunRef.current;
  };

  const isActiveUploadRun = (runToken) => activeUploadRunRef.current === runToken;

  const resetChatSession = () => {
    activeUploadRunRef.current += 1;
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        text: 'Ask naturally. I can tell whether you want clause-type examples or clauses similar to language you paste.',
      },
    ]);
    setInput('');
    setLoading(false);
    setContractLoading(false);
    setComparisonLoading(false);
    setContractJob(null);
    setContractFindings(null);
    setContractAgreementType('');
    setContractAgreementTypeDetail('');
    setComparisonJob(null);
    setComparisonSummary(null);
    setCompactPane('chat');
  };

  const submitMessage = async (messageText = input) => {
    const cleanMessage = messageText.trim();
    if (!cleanMessage) {
      toast.error('Ask for a clause type or paste clause language.');
      return;
    }
    setInput('');
    setLoading(true);
    setCompactPane('chat');
    setMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        role: 'user',
        text: cleanMessage,
      },
    ]);
    try {
      const previousClausePayload = latestClausePayload(messages);
      if (comparisonSummary && comparisonJob?.job_id && isClosestHistoricalFollowup(cleanMessage, previousClausePayload)) {
        setMessages((current) => [
          ...current,
          closestHistoricalClauseMessage(cleanMessage, previousClausePayload),
        ]);
        return;
      }
      if (comparisonSummary && comparisonJob?.job_id && looksLikeComparisonSimilarityRequest(cleanMessage)) {
        await submitComparisonSimilaritySearch(cleanMessage);
        return;
      }
      if (comparisonSummary && comparisonJob?.job_id && !looksLikeClauseLibraryRequest(cleanMessage)) {
        await submitComparisonFollowup(cleanMessage);
        return;
      }
      if (contractJob?.status === 'succeeded' && looksLikeUploadedContractSimilarityRequest(cleanMessage)) {
        await submitUploadedContractSimilaritySearch(cleanMessage);
        return;
      }
      if (contractJob?.status === 'succeeded' && looksLikeUploadedContractRiskQuestion(cleanMessage)) {
        await submitUploadedContractFollowup(cleanMessage);
        return;
      }
      if (contractJob?.status === 'succeeded' && !looksLikeClauseLibraryRequest(cleanMessage)) {
        await submitUploadedContractFollowup(cleanMessage);
        return;
      }
      const response = await fetch(`${CONTRACT_QA_API_BASE}/ai-chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: cleanMessage, limit: DEFAULT_LIMIT, ...buildChatContext(messages) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'AI clause chat failed.');
      }
      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          text: buildAssistantText(data),
          typing: true,
          payload: data,
        },
      ]);
    } catch (error) {
      console.error('AI clause chat failed:', error);
      setMessages((current) => [
        ...current,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          kind: 'error',
          text: error.message || 'AI clause chat failed.',
        },
      ]);
      toast.error(error.message || 'AI clause chat failed.');
    } finally {
      setLoading(false);
    }
  };

  const submitComparisonSimilaritySearch = async (cleanMessage) => {
    const resolveResponse = await fetch(`${CONTRACT_QA_API_BASE}/compare/jobs/${comparisonJob.job_id}/resolve-clause`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: cleanMessage }),
    });
    const resolved = await resolveResponse.json().catch(() => ({}));
    if (!resolveResponse.ok) throw new Error(resolved.detail || 'Could not identify the relevant clause in this comparison.');

    const searchResponse = await fetch(`${CONTRACT_QA_API_BASE}/classify-and-search`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query_text: resolved.query_text,
        title: resolved.title || 'Comparison clause similarity query',
        override_clause_type: resolved.clause_type || undefined,
        limit: DEFAULT_LIMIT,
      }),
    });
    const data = await searchResponse.json().catch(() => ({}));
    if (!searchResponse.ok) throw new Error(data.detail || 'Historical clause search failed.');

    const payload = {
      ...data,
      assistant_message: `I used the ${resolved.document_side} contract ${formatClauseType(resolved.clause_type || data.selected_clause_type)} clause as the search reference.`,
      intent: {
        intent: 'find_similar_clause',
        confidence: resolved.confidence || 0.8,
        user_facing_summary: 'Find historical clauses similar to a clause from the comparison.',
      },
      comparison_resolved_clause: resolved,
    };
    setMessages((current) => [
      ...current,
      {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: buildAssistantText(payload),
        typing: true,
        payload,
      },
    ]);
  };


  const submitUploadedContractSimilaritySearch = async (cleanMessage) => {
    const resolveResponse = await fetch(`${CONTRACT_QA_API_BASE}/review/jobs/${contractJob.job_id}/resolve-clause`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: cleanMessage }),
    });
    const resolved = await resolveResponse.json().catch(() => ({}));
    if (!resolveResponse.ok) throw new Error(resolved.detail || 'Could not identify the relevant clause in this contract.');

    const searchResponse = await fetch(`${CONTRACT_QA_API_BASE}/classify-and-search`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query_text: resolved.query_text,
        title: resolved.title || 'Uploaded contract clause similarity query',
        override_clause_type: resolved.clause_type || undefined,
        limit: DEFAULT_LIMIT,
        excluded_matches: resolved.excluded_matches || [],
      }),
    });
    const data = await searchResponse.json().catch(() => ({}));
    if (!searchResponse.ok) throw new Error(data.detail || 'Historical clause search failed.');

    const payload = {
      ...data,
      assistant_message: `I used the uploaded contract ${formatClauseType(resolved.clause_type || data.selected_clause_type)} clause as the search reference.`,
      intent: {
        intent: 'find_similar_clause',
        confidence: resolved.confidence || 0.8,
        user_facing_summary: 'Find historical clauses similar to a clause from the uploaded contract.',
      },
      uploaded_contract_resolved_clause: resolved,
    };
    setMessages((current) => [
      ...current,
      {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: buildAssistantText(payload),
        typing: true,
        payload,
      },
    ]);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  };


  const startUploadedContractReview = async () => {
    if (!contractFile) {
      toast.error('Upload a contract for Q&A.');
      return;
    }
    if (!contractAgreementType) {
      toast.error('Select a contract type.');
      return;
    }
    const runToken = startUploadRun();
    setCompactPane('chat');
    setContractLoading(true);
    setContractFindings(null);
    setComparisonJob(null);
    setComparisonSummary(null);
    setComparisonModalOpen(false);
    setUploadModalMode(null);
    const contractStartedAt = Date.now();
    const contractMessageId = `contract-review-${contractStartedAt}`;
    setMessages((current) => [
      ...current,
      {
        id: `contract-upload-${contractStartedAt}`,
        role: 'user',
        text: `Uploaded contract for Q&A:
Contract: ${contractFile.name}
Contract type: ${agreementTypeLabel(contractAgreementType)}`,
      },
      {
        id: contractMessageId,
        role: 'assistant',
        kind: 'contract-review',
        text: 'I’m reviewing the contract now.',
        job: { status: 'queued', stage: 'queued', progress_message: 'Review queued.' },
        findings: null,
      },
    ]);
    try {
      const formData = new FormData();
      formData.append('actor_id', 'sylvan-contract-qa');
      formData.append('agreement_type', contractAgreementType);
      if (contractAgreementType === 'other' && contractAgreementTypeDetail.trim()) {
        formData.append('agreement_type_detail', contractAgreementTypeDetail.trim());
      }
      formData.append('document', contractFile);
      const response = await fetch(`${CONTRACT_QA_API_BASE}/review/jobs`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Contract review upload failed.');
      if (!isActiveUploadRun(runToken)) return;
      setContractJob(data);
      updateComparisonMessage(contractMessageId, { job: data });
      await pollUploadedContractJob(data.job_id, contractMessageId, runToken);
    } catch (error) {
      if (!isActiveUploadRun(runToken)) return;
      console.error('Contract review failed:', error);
      toast.error(error.message || 'Contract review failed.');
      setContractJob((current) => ({ ...(current || {}), status: 'failed', error: error.message }));
      updateComparisonMessage(contractMessageId, {
        text: 'Contract review failed.',
        job: { status: 'failed', error: error.message },
      });
    } finally {
      if (isActiveUploadRun(runToken)) setContractLoading(false);
    }
  };

  const pollUploadedContractJob = async (jobId, messageId, runToken) => {
    for (let attempt = 0; attempt < 240; attempt += 1) {
      await delay(attempt === 0 ? 1000 : 5000);
      if (!isActiveUploadRun(runToken)) return;
      const statusResponse = await fetch(`${CONTRACT_QA_API_BASE}/review/jobs/${jobId}`, {
        credentials: 'include',
      });
      const statusData = await statusResponse.json().catch(() => ({}));
      if (!isActiveUploadRun(runToken)) return;
      if (!statusResponse.ok) throw new Error(statusData.detail || 'Could not load contract review status.');
      setContractJob(statusData);
      updateComparisonMessage(messageId, { job: statusData });
      if (statusData.status === 'succeeded') {
        const findingsResponse = await fetch(`${CONTRACT_QA_API_BASE}/review/jobs/${jobId}/findings`, {
          credentials: 'include',
        });
        const findingsData = await findingsResponse.json().catch(() => ({}));
        if (!isActiveUploadRun(runToken)) return;
        if (!findingsResponse.ok) throw new Error(findingsData.detail || 'Could not load contract findings.');
        setContractFindings(findingsData);
        setCompactPane('results');
        updateComparisonMessage(messageId, {
          text: buildContractReviewAssistantBlurb(
            findingsData,
            contractFile?.name,
            statusData.agreement_type_warning
          ),
          job: statusData,
          findings: findingsData,
        });
        return;
      }
      if (statusData.status === 'failed') {
        throw new Error(statusData.error || 'Contract review job failed.');
      }
    }
    throw new Error('Contract review timed out while waiting for the API.');
  };

  const startComparison = async () => {
    if (!comparisonBaseFile || !comparisonRevisedFile) {
      toast.error('Upload both contracts to compare.');
      return;
    }
    if (!comparisonAgreementType) {
      toast.error('Select a contract type.');
      return;
    }
    const runToken = startUploadRun();
    setCompactPane('chat');
    setComparisonLoading(true);
    setComparisonSummary(null);
    setContractJob(null);
    setContractFindings(null);
    setComparisonModalOpen(false);
    setUploadModalMode(null);
    const comparisonStartedAt = Date.now();
    const comparisonMessageId = `comparison-${comparisonStartedAt}`;
    setMessages((current) => [
      ...current,
      {
        id: `comparison-upload-${comparisonStartedAt}`,
        role: 'user',
        text: `Uploaded contracts for comparison:\nContract A: ${comparisonBaseFile.name}\nContract B: ${comparisonRevisedFile.name}\nContract type: ${agreementTypeLabel(comparisonAgreementType)}`,
      },
      {
        id: comparisonMessageId,
        role: 'assistant',
        kind: 'comparison',
        text: 'I’m comparing the two contracts now.',
        job: { status: 'queued', stage: 'queued', progress_message: 'Comparison queued.' },
        summary: null,
      },
    ]);
    try {
      const formData = new FormData();
      formData.append('actor_id', 'sylvan-contract-qa');
      formData.append('base_document', comparisonBaseFile);
      formData.append('revised_document', comparisonRevisedFile);
      formData.append('agreement_type', comparisonAgreementType);
      if (comparisonAgreementType === 'other' && comparisonAgreementTypeDetail.trim()) {
        formData.append('agreement_type_detail', comparisonAgreementTypeDetail.trim());
      }
      const response = await fetch(`${CONTRACT_QA_API_BASE}/compare/jobs`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Comparison upload failed.');
      if (!isActiveUploadRun(runToken)) return;
      setComparisonJob(data);
      updateComparisonMessage(comparisonMessageId, { job: data });
      await pollComparisonJob(data.job_id, comparisonMessageId, runToken);
    } catch (error) {
      if (!isActiveUploadRun(runToken)) return;
      console.error('Contract comparison failed:', error);
      toast.error(error.message || 'Contract comparison failed.');
      setComparisonJob((current) => ({ ...(current || {}), status: 'failed', error: error.message }));
      updateComparisonMessage(comparisonMessageId, {
        text: 'Comparison failed.',
        job: { status: 'failed', error: error.message },
      });
    } finally {
      if (isActiveUploadRun(runToken)) setComparisonLoading(false);
    }
  };

  const pollComparisonJob = async (jobId, messageId, runToken) => {
    for (let attempt = 0; attempt < 240; attempt += 1) {
      await delay(attempt === 0 ? 1000 : 5000);
      if (!isActiveUploadRun(runToken)) return;
      const statusResponse = await fetch(`${CONTRACT_QA_API_BASE}/compare/jobs/${jobId}`, {
        credentials: 'include',
      });
      const statusData = await statusResponse.json().catch(() => ({}));
      if (!isActiveUploadRun(runToken)) return;
      if (!statusResponse.ok) throw new Error(statusData.detail || 'Could not load comparison status.');
      setComparisonJob(statusData);
      updateComparisonMessage(messageId, { job: statusData });
      if (statusData.status === 'succeeded') {
        const summaryResponse = await fetch(`${CONTRACT_QA_API_BASE}/compare/jobs/${jobId}/summary`, {
          credentials: 'include',
        });
        const summaryData = await summaryResponse.json().catch(() => ({}));
        if (!isActiveUploadRun(runToken)) return;
        if (!summaryResponse.ok) throw new Error(summaryData.detail || 'Could not load comparison summary.');
        setComparisonSummary(summaryData);
        setCompactPane('results');
        updateComparisonMessage(messageId, {
          text: buildComparisonAssistantBlurb(summaryData, comparisonBaseFile?.name, comparisonRevisedFile?.name),
          job: statusData,
          summary: summaryData,
        });
        return;
      }
      if (statusData.status === 'failed') {
        throw new Error(statusData.error || 'Comparison job failed.');
      }
    }
    throw new Error('Comparison timed out while waiting for the API.');
  };

  const submitComparisonFollowup = async (cleanQuestion) => {
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}/compare/jobs/${comparisonJob.job_id}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: cleanQuestion, history: comparisonHistory(messages) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Comparison follow-up failed.');
      setMessages((current) => [
        ...current,
        {
          id: `comparison-answer-${Date.now()}`,
          role: 'assistant',
          text: data.answer,
          cited_difference_ids: data.cited_difference_ids || [],
        },
      ]);
    } catch (error) {
      console.error('Comparison follow-up failed:', error);
      toast.error(error.message || 'Comparison follow-up failed.');
      setMessages((current) => [
        ...current,
        {
          id: `comparison-error-${Date.now()}`,
          role: 'assistant',
          kind: 'error',
          text: error.message || 'Comparison follow-up failed.',
        },
      ]);
    }
  };


  const submitUploadedContractFollowup = async (cleanQuestion) => {
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}/review/jobs/${contractJob.job_id}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: cleanQuestion, history: contractHistory(messages) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Uploaded contract follow-up failed.');
      setMessages((current) => [
        ...current,
        {
          id: `contract-answer-${Date.now()}`,
          role: 'assistant',
          text: data.answer,
          cited_unit_ids: data.cited_unit_ids || [],
          cited_finding_ids: data.cited_finding_ids || [],
        },
      ]);
    } catch (error) {
      console.error('Uploaded contract follow-up failed:', error);
      toast.error(error.message || 'Uploaded contract follow-up failed.');
      setMessages((current) => [
        ...current,
        {
          id: `contract-error-${Date.now()}`,
          role: 'assistant',
          kind: 'error',
          text: error.message || 'Uploaded contract follow-up failed.',
        },
      ]);
    }
  };

  const updateComparisonMessage = (messageId, updates) => {
    setMessages((current) =>
      current.map((message) => (message.id === messageId ? { ...message, ...updates } : message))
    );
  };

  const activeMode = comparisonSummary ? 'comparison' : contractJob ? 'contract' : 'library';
  const workspaceTitle = activeMode === 'comparison'
      ? 'Contract Comparison'
      : activeMode === 'contract'
        ? 'Contract Review'
        : 'Clause Search';
  const workspaceSubtitle = activeMode === 'comparison'
    ? `${comparisonBaseFile?.name || 'Contract A'} → ${comparisonRevisedFile?.name || 'Contract B'}`
    : activeMode === 'contract'
      ? contractFile?.name || 'Uploaded contract'
      : 'Find similar historical clauses or upload contracts for AI review.';
  const hasCompletedAnalysis = comparisonSummary || contractJob?.status === 'succeeded';

  return (
    <div className="h-full max-h-[calc(100dvh-4rem)] min-h-0 w-full overflow-hidden bg-gray-50 text-gray-900">
      <div className="flex h-full flex-col">
        <header className="shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-5 sm:py-4 min-[1400px]:px-6">
          <div className="flex flex-col gap-3 min-[1400px]:flex-row min-[1400px]:items-center min-[1400px]:justify-between">
            <div className="min-w-0">
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                <h1 className="text-lg font-bold text-gray-950 sm:text-xl">{workspaceTitle}</h1>
                <span className="hidden text-gray-300 sm:inline">·</span>
                {activeMode === 'comparison' ? (
                  <div className="flex w-full min-w-0 items-center gap-1.5 text-xs font-medium text-gray-500 sm:w-auto sm:gap-2 sm:text-sm">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />
                    <span className="min-w-0 max-w-[40%] truncate sm:max-w-[220px]">{comparisonBaseFile?.name || 'Contract A'}</span>
                    <span className="shrink-0 text-gray-300">→</span>
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-purple-500" />
                    <span className="min-w-0 max-w-[40%] truncate sm:max-w-[220px]">{comparisonRevisedFile?.name || 'Contract B'}</span>
                  </div>
                ) : (
                  <p className="min-w-0 truncate text-sm font-medium text-gray-500">{workspaceSubtitle}</p>
                )}
              </div>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 min-[1400px]:w-auto">
              {hasCompletedAnalysis && (
                <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-semibold text-sylvan-700">
                  <Sparkles className="mr-2" size={15} />
                  AI Analyzed
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  setUploadModalMode(null);
                  setComparisonModalOpen(true);
                }}
                disabled={comparisonLoading || contractLoading}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-sylvan-700 shadow-sm hover:bg-orange-50 disabled:opacity-50 sm:flex-none"
              >
                <Plus className="mr-2" size={15} />
                Upload
              </button>
              <button
                type="button"
                onClick={resetChatSession}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-sylvan-700 shadow-sm hover:bg-orange-50 sm:flex-none"
              >
                <RefreshCw className="mr-2" size={15} />
                New chat
              </button>
            </div>
          </div>
        </header>

        <div className="grid shrink-0 grid-cols-2 border-b border-gray-200 bg-white p-1 min-[1400px]:hidden" role="tablist" aria-label="Clause Search view">
          <button
            type="button"
            role="tab"
            aria-selected={compactPane === 'results'}
            onClick={() => setCompactPane('results')}
            className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold ${compactPane === 'results' ? 'bg-orange-50 text-sylvan-700' : 'text-gray-500'}`}
          >
            <FileText size={16} /> Results
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={compactPane === 'chat'}
            onClick={() => setCompactPane('chat')}
            className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold ${compactPane === 'chat' ? 'bg-orange-50 text-sylvan-700' : 'text-gray-500'}`}
          >
            <MessageSquare size={16} /> Chat
          </button>
        </div>

        <div className={`grid min-h-0 flex-1 grid-cols-1 overflow-hidden ${assistantExpanded ? '' : 'min-[1400px]:grid-cols-[minmax(0,1fr)_420px]'}`}>
          {!assistantExpanded && <main className={`${compactPane === 'results' ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-col overflow-x-hidden overflow-y-auto bg-gray-50 px-3 py-4 sm:px-5 sm:py-5 min-[1400px]:flex min-[1400px]:px-6 min-[1400px]:py-6`}>
            {activeMode === 'comparison' ? (
              <ComparisonWorkspace
                summary={comparisonSummary}
                job={comparisonJob}
                baseFile={comparisonBaseFile}
                revisedFile={comparisonRevisedFile}
                onPromptClick={submitMessage}
              />
            ) : activeMode === 'contract' ? (
              <ContractWorkspace
                job={contractJob}
                file={contractFile}
                findings={contractFindings?.findings || []}
                onPromptClick={submitMessage}
              />
            ) : (
              <ClauseLibraryWorkspace />
            )}
          </main>}

          <AssistantPanel
            expanded={assistantExpanded}
            compactVisible={compactPane === 'chat'}
            onToggleExpanded={() => setAssistantExpanded((current) => !current)}
            messages={messages}
            loading={loading}
            input={input}
            onInputChange={setInput}
            onKeyDown={handleKeyDown}
            onSubmit={() => submitMessage()}
            onPromptClick={submitMessage}
            contractJob={contractJob}
            comparisonSummary={comparisonSummary}
            onEndContract={() => {
              setContractJob(null);
              setContractFindings(null);
            }}
            onEndComparison={() => {
              setComparisonJob(null);
              setComparisonSummary(null);
            }}
            onRetryContractReview={startUploadedContractReview}
            onRetryComparison={startComparison}
            contractLoading={contractLoading}
            comparisonLoading={comparisonLoading}
            messagesEndRef={messagesEndRef}
          />


        </div>
      </div>
      {comparisonModalOpen && uploadModalMode === null && (
        <UploadModeChoiceModal
          onSelectContract={() => {
            setContractFile(null);
            setContractAgreementType('');
            setContractAgreementTypeDetail('');
            setUploadModalMode('contract');
          }}
          onSelectComparison={() => {
            setComparisonAgreementType('');
            setComparisonAgreementTypeDetail('');
            setUploadModalMode('comparison');
          }}
          onClose={() => setComparisonModalOpen(false)}
        />
      )}
      {comparisonModalOpen && uploadModalMode === 'contract' && (
        <ContractUploadModal
          contractFile={contractFile}
          agreementType={contractAgreementType}
          agreementTypeDetail={contractAgreementTypeDetail}
          loading={contractLoading}
          onContractFileChange={setContractFile}
          onAgreementTypeChange={(value) => {
            setContractAgreementType(value);
            if (value !== 'other') setContractAgreementTypeDetail('');
          }}
          onAgreementTypeDetailChange={setContractAgreementTypeDetail}
          onBack={() => setUploadModalMode(null)}
          onClose={() => setComparisonModalOpen(false)}
          onStart={startUploadedContractReview}
        />
      )}
      {comparisonModalOpen && uploadModalMode === 'comparison' && (
        <ComparisonUploadModal
          baseFile={comparisonBaseFile}
          revisedFile={comparisonRevisedFile}
          agreementType={comparisonAgreementType}
          agreementTypeDetail={comparisonAgreementTypeDetail}
          loading={comparisonLoading}
          onBaseFileChange={setComparisonBaseFile}
          onRevisedFileChange={setComparisonRevisedFile}
          onAgreementTypeChange={(value) => {
            setComparisonAgreementType(value);
            if (value !== 'other') setComparisonAgreementTypeDetail('');
          }}
          onAgreementTypeDetailChange={setComparisonAgreementTypeDetail}
          onBack={() => setUploadModalMode(null)}
          onClose={() => setComparisonModalOpen(false)}
          onStart={startComparison}
        />
      )}
    </div>
  );
};

const AssistantPanel = ({
  expanded,
  compactVisible,
  onToggleExpanded,
  messages,
  loading,
  input,
  onInputChange,
  onKeyDown,
  onSubmit,
  onPromptClick,
  contractJob,
  comparisonSummary,
  onEndContract,
  onEndComparison,
  onRetryContractReview,
  onRetryComparison,
  contractLoading,
  comparisonLoading,
  messagesEndRef,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messageListRef = useRef(null);
  const pendingScrollAnchorRef = useRef(null);
  const previousMessageCountRef = useRef(messages.length);
  const suggestedPrompts = (
    comparisonSummary
      ? COMPARISON_FOLLOW_UP_PROMPTS
      : contractJob?.status === 'succeeded'
        ? CONTRACT_FOLLOW_UP_PROMPTS
        : EXAMPLE_PROMPTS
  ).slice(0, 4);

  useEffect(() => {
    const previousMessageCount = previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;
    if (messages.length > previousMessageCount) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length, messagesEndRef]);

  useEffect(() => {
    const anchor = pendingScrollAnchorRef.current;
    if (!anchor) return undefined;

    let frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => {
        const container = messageListRef.current;
        if (!container) return;
        const messageNode = Array.from(container.querySelectorAll('[data-chat-message-id]'))
          .find((node) => node.dataset.chatMessageId === anchor.messageId);
        if (!messageNode) return;

        const containerTop = container.getBoundingClientRect().top;
        const messageTop = messageNode.getBoundingClientRect().top;
        container.scrollTop += messageTop - containerTop - anchor.offsetTop;
      });
    });

    pendingScrollAnchorRef.current = null;
    return () => window.cancelAnimationFrame(frame);
  }, [expanded]);

  const captureScrollAnchor = () => {
    const container = messageListRef.current;
    if (!container) return null;
    const containerTop = container.getBoundingClientRect().top;
    const messageNodes = Array.from(container.querySelectorAll('[data-chat-message-id]'));
    const visibleNode = messageNodes.find((node) => node.getBoundingClientRect().bottom >= containerTop);
    if (!visibleNode) return null;
    return {
      messageId: visibleNode.dataset.chatMessageId,
      offsetTop: visibleNode.getBoundingClientRect().top - containerTop,
    };
  };

  const handleToggleExpanded = () => {
    pendingScrollAnchorRef.current = captureScrollAnchor();
    onToggleExpanded();
  };

  return (
    <aside className={`${compactVisible ? 'flex' : 'hidden'} min-h-0 min-w-0 w-full flex-col border-gray-200 bg-white min-[1400px]:flex ${expanded ? '' : 'min-[1400px]:border-l'}`}>
      <div className="shrink-0 border-b border-gray-200 px-3 py-3 sm:px-5 sm:py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={handleToggleExpanded}
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 min-[1400px]:inline-flex"
              title={expanded ? 'Collapse AI assistant' : 'Expand AI assistant'}
              aria-label={expanded ? 'Collapse AI assistant' : 'Expand AI assistant'}
            >
              {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
              <Bot size={19} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-950">AI Assistant</h2>
              <p className="truncate text-sm text-gray-500">Ask about any clause or risk</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSuggestions((current) => !current)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${showSuggestions ? 'bg-orange-50 text-sylvan-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
              title={showSuggestions ? 'Hide suggested questions' : 'Show suggested questions'}
              aria-label={showSuggestions ? 'Hide suggested questions' : 'Show suggested questions'}
            >
              <Lightbulb size={16} />
            </button>
            <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 sm:px-3 sm:text-sm">Online</span>
          </div>
        </div>
      </div>

      <div ref={messageListRef} className={`min-h-0 flex-1 space-y-4 overscroll-contain overflow-x-hidden overflow-y-auto bg-white px-3 py-4 sm:px-5 sm:py-5 ${expanded ? 'mx-auto w-full max-w-5xl' : ''}`}>
        {messages.map((message) => (
          <div key={message.id} data-chat-message-id={message.id}>
            <ChatMessage
              message={message}
              onPromptClick={onPromptClick}
              onRetryContractReview={onRetryContractReview}
              onRetryComparison={onRetryComparison}
              contractLoading={contractLoading}
              comparisonLoading={comparisonLoading}
              compact
            />
          </div>
        ))}
        {loading && (
          <div className="flex items-center text-sm text-gray-600">
            <Loader2 className="mr-2 animate-spin text-sylvan-500" size={16} />
            Understanding request and searching memory
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-3 pt-3 sm:px-5 sm:pt-4" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <div className={expanded ? 'mx-auto w-full max-w-5xl' : ''}>
          <AssistantSessionPills
            contractJob={contractJob}
            comparisonSummary={comparisonSummary}
            onEndContract={onEndContract}
            onEndComparison={onEndComparison}
          />
          {showSuggestions && suggestedPrompts.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onPromptClick(prompt)}
                  disabled={loading}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-xs font-medium text-gray-600 hover:border-sylvan-200 hover:bg-orange-50 hover:text-sylvan-700 disabled:opacity-50"
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(event) => onInputChange(event.target.value)}
              onKeyDown={onKeyDown}
              rows={expanded ? 3 : 2}
              placeholder="Ask about any clause..."
              className="min-h-[44px] flex-1 resize-none rounded-xl border border-gray-200 bg-[#fbfaf8] px-3 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sylvan-500"
            />
            <button
              type="button"
              onClick={onSubmit}
              disabled={loading}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sylvan-500 text-white shadow-sm hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

const ClauseLibraryWorkspace = () => (
  <div className="mx-auto max-w-6xl space-y-6">
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="max-w-2xl">
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
          <Sparkles size={20} />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-950">Ask about clauses, risks, or uploaded contracts</h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          Use the assistant to find historical clauses, upload one contract for grounded Q&A, or compare two contracts side by side.
        </p>
      </div>
    </div>
  </div>
);

const ContractWorkspace = ({ job, file, findings }) => {
  const highCount = findings.filter((finding) => finding.risk_level === 'high').length;
  const mediumCount = findings.filter((finding) => finding.risk_level === 'medium').length;
  const lowCount = findings.filter((finding) => finding.risk_level === 'low').length;
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <WorkspaceTab icon={<FileText size={16} />} active label="Review Summary" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <WorkspaceMetric title="Risk Findings" value={findings.length || 0} tone="orange" />
        <WorkspaceMetric title="High Risk" value={highCount} tone="red" />
        <WorkspaceMetric title="Medium Risk" value={mediumCount} tone="amber" />
        <WorkspaceMetric title="Low Risk" value={lowCount} tone="green" />
      </div>
      {job?.agreement_type_warning && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          <AlertTriangle className="mt-0.5 shrink-0" size={18} />
          <div>
            <p className="font-semibold">Contract type may not match</p>
            <p className="mt-1 leading-5">{job.agreement_type_warning}</p>
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
            <Sparkles size={19} />
          </span>
          <div className="min-w-0">
            <p className="text-lg font-bold text-gray-950">AI Review</p>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {job?.status === 'succeeded'
                ? contractReviewSummaryText(findings, file?.name)
                : job?.error || job?.progress_message || formatJobStatus(job)}
            </p>
          </div>
        </div>
      </div>
      <ContractFindingsSummary findings={findings} findingCount={findings.length} />
    </div>
  );
};

export const NdaWorkspace = ({ job, review, fileName }) => {
  const [activeView, setActiveView] = useState('summary');
  const [emailingLegal, setEmailingLegal] = useState(false);
  if (!review) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <Loader2 className="mt-0.5 shrink-0 animate-spin text-sylvan-500" size={20} />
            <div>
              <h2 className="font-bold text-gray-950">NDA review in progress</h2>
              <p className="mt-1 text-sm text-gray-600">{job?.error || job?.progressMessage || formatNdaJobStatus(job)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const escalationActions = buildNdaEscalationActions(review);
  const legalReviewReasons = buildNdaLegalReviewReasons(review, escalationActions);
  const advisoryConcerns = buildNdaAdvisoryConcerns(review);
  const disposition = ndaDispositionDisplay(review.disposition);
  const scope = ndaScopeDisplay(review);
  const unusualFillIns = review.unusualFillIns || [];
  const metrics = {
    checks: review.ruleResults.length,
    edits: review.proposedEdits.length,
    triggers: escalationActions.length,
    advisories: advisoryConcerns.length,
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
        <button type="button" onClick={() => setActiveView('summary')} className="min-w-0">
          <WorkspaceTab icon={<MessageSquare size={16} />} active={activeView === 'summary'} label="Summary" />
        </button>
        <button type="button" onClick={() => setActiveView('actions')} className="min-w-0">
          <WorkspaceTab
            icon={<AlertTriangle size={16} />}
            active={activeView === 'actions'}
            label="Required Actions"
            badge={`${metrics.edits + metrics.triggers + legalReviewReasons.length}`}
          />
        </button>
        <button type="button" onClick={() => setActiveView('checklist')} className="min-w-0">
          <WorkspaceTab
            icon={<FileText size={16} />}
            active={activeView === 'checklist'}
            label="Full Checklist"
            badge={`${metrics.checks}`}
          />
        </button>
      </div>

      <div className={`rounded-xl border px-4 py-4 sm:px-5 ${disposition.containerClass}`} role="status">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${disposition.iconClass}`}>
            {review.disposition === 'eligible_to_sign_under_guide'
              ? <CheckCircle2 size={19} />
              : <AlertTriangle size={19} />}
          </span>
          <div className="min-w-0">
            <p className="text-lg font-bold">{disposition.label}</p>
            <p className="mt-1 text-sm leading-6">{disposition.guidance}</p>
            <p className="mt-2 text-xs font-semibold leading-5">
              Guide eligibility is not legal approval, signature authority, or authorization to bind Sylvan.
            </p>
            {review.disposition === 'escalate_to_legal' && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <button
                  type="button"
                  disabled={emailingLegal}
                  onClick={async () => {
                    // Nothing is sent from here: the review copy downloads, then the
                    // reviewer's own mail program opens with the draft — they attach
                    // the download and decide whether and when to send.
                    setEmailingLegal(true);
                    let attachmentName = null;
                    try {
                      if (job?.jobId) attachmentName = await downloadNdaReviewCopy(job.jobId, fileName || job?.sourceFilename);
                    } catch (error) {
                      toast.error(`The annotated review copy could not be prepared: ${error.message}. The draft will open without it.`);
                    } finally {
                      setEmailingLegal(false);
                    }
                    window.location.href = buildNdaLegalEmailHref(review, {
                      fileName: fileName || job?.sourceFilename,
                      jobId: job?.jobId,
                      to: process.env.REACT_APP_NDA_LEGAL_EMAIL || '',
                      attachmentName,
                    });
                  }}
                  className="inline-flex min-h-[40px] items-center rounded-full border border-current bg-white/70 px-4 py-2 text-sm font-semibold hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {emailingLegal ? <Loader2 className="mr-2 shrink-0 animate-spin" size={15} /> : <Mail className="mr-2 shrink-0" size={15} />}
                  {emailingLegal ? 'Preparing review copy…' : 'Email Legal'}
                </button>
                <span className="text-xs leading-5 opacity-80">
                  Downloads the annotated review copy, then opens a pre-filled draft — attach the download before sending.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {unusualFillIns.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 text-amber-950 sm:px-5" role="alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={19} />
            <div className="min-w-0">
              <p className="font-bold">
                {unusualFillIns.length === 1 ? 'Unusual field to complete before signing' : 'Unusual fields to complete before signing'}
              </p>
              <ul className="mt-1 space-y-1 text-sm leading-6">
                {unusualFillIns.map((field) => {
                  const where = describeNdaFillInLocation(field);
                  return (
                    <li key={`${field.label}-${field.pageNumber}`}>
                      <span className="font-semibold">“{field.label}”</span>
                      {where ? ` — ${where}` : ''}
                      {(field.line || field.after) && (
                        <div className="mt-1 rounded-md border border-amber-200 bg-white/70 px-3 py-2 text-xs leading-5 text-amber-950">
                          {field.after && <p className="text-amber-800">Right after: {field.after}</p>}
                          {field.line && <p><span className="font-semibold">As printed:</span> {field.line}</p>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-sm leading-6">
                This is not a standard NDA field and may add substantive terms. Confirm with the requester what it should say before signing.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeView === 'summary' && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            <WorkspaceMetric title="Required Edits" value={metrics.edits} tone="amber" />
            <WorkspaceMetric title="Legal Triggers" value={metrics.triggers} tone="red" />
            <WorkspaceMetric title="Warnings" value={metrics.advisories} tone="orange" />
          </div>
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-bold text-gray-950">Document Assessment</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <NdaDerivedDetail
                label="Counterparty"
                value={review.documentAssessment?.derivedCounterparty || review.counterparty || job?.counterparty}
                confidence={review.documentAssessment?.counterpartyConfidence}
              />
              <NdaDerivedDetail
                label="Document-stated purpose"
                value={review.documentAssessment?.documentStatedPurpose || review.businessPurpose || job?.businessPurpose}
                confidence={review.documentAssessment?.purposeConfidence}
              />
              <NdaDetailRow label="Inferred flow" value={ndaInformationFlowLabel(review.documentAssessment?.inferredInformationFlow)} />
              {scope && <NdaDetailRow label="NDA scope" value={scope.label} />}
            </dl>
            {scope && (
              <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-700">
                <p className="font-semibold text-gray-900">{scope.effect}</p>
                {scope.rationale && <p className="mt-1">{scope.rationale}</p>}
                {scope.note && <p className="mt-1 text-gray-600">{scope.note}</p>}
              </div>
            )}
            {review.documentAssessment?.rationale && (
              <p className="mt-4 border-t border-gray-100 pt-4 text-sm leading-6 text-gray-600">{review.documentAssessment.rationale}</p>
            )}
            {review.intakeValidation?.consistent === false && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 shrink-0" size={17} />
                  <div>
                    <p className="font-bold">Intake information may conflict with the document</p>
                    {review.intakeValidation?.conflicts?.length ? (
                      <div className="mt-1 space-y-2 leading-6">
                        {review.intakeValidation.conflicts.map((conflict, index) => (
                          <p key={`${conflict}-${index}`}>{conflict}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 leading-6">The intake answers do not match the document assessment.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {activeView === 'actions' && (
        <div className="space-y-5">
          {legalReviewReasons.length > 0 && (
            <section className="rounded-lg border border-gray-200 bg-white px-5 py-4 text-gray-800">
              <h3 className="text-sm font-semibold">Reason for Legal Review</h3>
              <p className="mt-2 text-sm leading-6">
                {legalReviewReasons.map((reason) => reason.description).join(' ')}
              </p>
            </section>
          )}
          <NdaActionSection
            title="Legal Escalation Triggers"
            count={escalationActions.length}
            emptyText="No Legal escalation triggers were validated."
          >
            <div className="space-y-3">
              {escalationActions.map((action, index) => (
                <NdaEscalationActionRow
                  key={`${action.title}-${index}`}
                  action={action}
                  defaultOpen={index === 0}
                />
              ))}
            </div>
          </NdaActionSection>
          <NdaActionSection
            title="Validated Routine Edits"
            count={review.proposedEdits.length}
            emptyText="No routine edits are required by the validated review."
          >
            <div className="space-y-3">
              {review.proposedEdits.map((edit, index) => (
                <NdaEditCard
                  key={edit.editId}
                  edit={edit}
                  ruleCatalog={review.ruleCatalog}
                  severity={ndaRuleSeverity(review, edit.authorizationRuleId)}
                  defaultOpen={escalationActions.length === 0 && legalReviewReasons.length === 0 && index === 0}
                />
              ))}
            </div>
          </NdaActionSection>
          {advisoryConcerns.length > 0 && (
            <NdaActionSection
              title="Warnings"
              count={advisoryConcerns.length}
            >
              <div className="space-y-3">
                {advisoryConcerns.map((concern, index) => (
                  <NdaAdvisoryConcernRow
                    key={`${concern.title}-${index}`}
                    concern={concern}
                  />
                ))}
              </div>
            </NdaActionSection>
          )}
        </div>
      )}

      {activeView === 'checklist' && (
        <NdaChecklist ruleResults={review.ruleResults} ruleCatalog={review.ruleCatalog} />
      )}
    </div>
  );
};

const NdaDetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="font-semibold text-gray-500">{label}</dt>
    <dd className="text-right font-medium text-gray-900">{value || 'Not available'}</dd>
  </div>
);

const NdaDerivedDetail = ({ label, value, confidence }) => {
  const confidenceLabel = value && Number.isFinite(confidence)
    ? `${Math.round(confidence * 100)}% confidence`
    : null;
  return (
    <div className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
      <div className="flex items-start justify-between gap-4">
        <dt className="font-semibold text-gray-500">{label}</dt>
        <dd className="max-w-[70%] text-right font-medium text-gray-900">{value || 'Not identified'}</dd>
      </div>
      {confidenceLabel && (
        <div className="mt-1 text-right text-xs leading-5 text-gray-500">
          <span>{confidenceLabel}</span>
        </div>
      )}
    </div>
  );
};

const NdaNoticeSection = ({ title, items = [], tone, emptyText }) => {
  if (!items.length && !emptyText) return null;
  return (
    <section>
      <h2 className="mb-3 text-base font-bold text-gray-950">{title}</h2>
      {items.length ? (
        <NdaNoticeList items={items} tone={tone} />
      ) : (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">{emptyText}</div>
      )}
    </section>
  );
};

const NdaNoticeList = ({ items, tone = 'gray', className = '' }) => {
  const toneClass = {
    red: 'border-rose-200 bg-rose-50 text-rose-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    gray: 'border-gray-200 bg-white text-gray-700',
  }[tone];
  return (
    <div className={`space-y-2 ${className}`}>
      {items.map((item, index) => (
        <div key={`${item}-${index}`} className={`rounded-xl border px-4 py-3 text-sm leading-6 ${toneClass}`}>{item}</div>
      ))}
    </div>
  );
};

const NdaActionSection = ({ title, count, emptyText, children }) => (
  <section>
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-base font-bold text-gray-950">{title}</h2>
      <span className="text-xs font-semibold text-gray-500">{count} {count === 1 ? 'action' : 'actions'}</span>
    </div>
    {count ? children : (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
        {emptyText}
      </div>
    )}
  </section>
);

const NDA_SEVERITY_RANK = { advisory: 0, elevated: 1, critical: 2 };

// The resolved level of one rule's finding (policy v5 onward); null for older reviews.
export const ndaRuleSeverity = (review, ruleId) => {
  const severity = (review.ruleResults || []).find((item) => item.ruleId === ruleId)?.severity;
  return NDA_SEVERITY_RANK[severity] !== undefined ? severity : null;
};

export const formatNdaSeverity = (severity) => (
  severity ? `${severity.charAt(0).toUpperCase()}${severity.slice(1)}` : ''
);

const NDA_SCOPE_LABELS = { standard: 'Standard', government: 'Government', small_business: 'Small business' };
const NDA_SCOPE_EFFECT = {
  standard: 'Findings keep their standard severity.',
  government: 'Findings are raised one level, for example Elevated to Critical.',
  small_business: 'Findings are lowered one level. Every rule is still checked.',
};

// The NDA's scope as it actually shaped severity (policy v5 onward); null for older
// reviews. Built from the applied scope, not the model's raw reading, so a reading that
// was not confident enough to count is shown as exactly that rather than as an effect.
export const ndaScopeDisplay = (review) => {
  const applied = review?.appliedScope;
  if (!applied) return null;
  const assessment = review.documentAssessment || {};
  const inferred = assessment.inferredScope || 'standard';
  const confidence = Number(assessment.scopeConfidence);
  const percent = Number.isFinite(confidence) ? Math.round(confidence * 100) : null;
  return {
    label: NDA_SCOPE_LABELS[applied] || applied,
    effect: NDA_SCOPE_EFFECT[applied] || '',
    rationale: applied === 'standard' && inferred === 'standard' ? '' : (assessment.scopeRationale || ''),
    note: inferred !== applied
      ? `The agent read this as ${NDA_SCOPE_LABELS[inferred] || inferred}${percent !== null ? ` (${percent}% confident)` : ''}, `
        + 'which was not confident enough to change severity, so Standard was used.'
      : '',
  };
};

// Where a fill-in is, in words a reader can find in their own copy: "the last line of
// the document, in the signature block, page 1". A page number alone can mislead when a
// document is paginated differently from the reader's copy.
export const describeNdaFillInLocation = (field) => {
  const page = field.pageNumber ? `page ${field.pageNumber}` : '';
  return [field.location, page].filter(Boolean).join(', ');
};

const truncateNdaText = (text, limit) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > limit ? `${clean.slice(0, limit - 1).trimEnd()}…` : clean;
};

// Generate (or reuse) the escalated review's annotated copy and hand it to the browser
// as a download, so the reviewer can attach it to the Legal draft. The review service
// reuses the same generated copy for repeated requests on one review.
export const downloadNdaReviewCopy = async (jobId, fileName) => {
  const submit = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/redlines`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ manualValues: {} }),
  });
  const submission = await submit.json().catch(() => ({}));
  if (!submit.ok) throw new Error(submission.detail || 'The review copy could not be started.');
  const redlineId = submission.redlineId;
  const deadline = Date.now() + 90000;
  for (;;) {
    const poll = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/redlines/${redlineId}`, { credentials: 'include' });
    const status = await poll.json().catch(() => ({}));
    if (!poll.ok) throw new Error(status.detail || 'The review copy status could not be read.');
    if (status.status === 'succeeded') break;
    if (status.status === 'failed') throw new Error(status.error || 'The review copy could not be generated.');
    if (Date.now() > deadline) throw new Error('Timed out generating the review copy.');
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }
  // The copy keeps the upload's format: a .docx with Word comments, or a PDF with
  // margin notes.
  const listing = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/redlines/${redlineId}/artifacts`, { credentials: 'include' });
  const artifacts = (await listing.json().catch(() => ({}))).artifacts || [];
  const copy = artifacts.find((item) => /^nda_review_copy\.(?:docx|pdf)$/i.test(item.name));
  if (!listing.ok || !copy) throw new Error('The review copy could not be found.');
  const response = await fetch(
    `${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/redlines/${redlineId}/artifacts/${encodeURIComponent(copy.name)}`,
    { credentials: 'include' },
  );
  if (!response.ok) throw new Error('The review copy could not be downloaded.');
  const blob = await response.blob();
  const extension = copy.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'docx';
  const downloadName = `${String(fileName || 'NDA').replace(/\.(docx|pdf|doc)$/i, '')} — review copy.${extension}`;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = downloadName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  return downloadName;
};

// A pre-filled email to Legal, opened in the reviewer's own mail program: nothing is sent
// from here. Per Britney (Sep 2026), escalating should come with a way to hand Legal the
// concise references — what the NDA is, what was flagged and where — without retyping.
// Kept short because mail programs truncate long mailto links.
export const buildNdaLegalEmailHref = (review, { fileName, jobId, to = '', attachmentName = null } = {}) => {
  const actions = buildNdaEscalationActions(review);
  const scope = ndaScopeDisplay(review);
  const counterparty = review.documentAssessment?.derivedCounterparty;
  const lines = [
    'Hi,',
    '',
    'The NDA Agent flagged this NDA for Legal review. Findings, most serious first:',
    '',
    `Document: ${fileName || 'NDA document'}`,
    ...(counterparty ? [`Counterparty: ${counterparty}`] : []),
    ...(scope ? [`NDA scope: ${scope.label}`] : []),
    ...(attachmentName ? [
      '',
      `Attached: ${attachmentName} — the NDA with every finding as a margin comment.`,
      '(The file was just downloaded; please attach it to this email.)',
    ] : []),
    '',
  ];
  actions.slice(0, 8).forEach((action, index) => {
    const level = action.severity ? `[${formatNdaSeverity(action.severity)}] ` : '';
    const evidence = (action.evidence || [])[0];
    const pages = evidence?.pageNumbers?.length ? ` (${formatNdaPages(evidence.pageNumbers)})` : '';
    lines.push(`${index + 1}. ${level}${action.title}${pages}`);
    if (evidence?.exactQuote) lines.push(`   "${truncateNdaText(evidence.exactQuote, 160)}"`);
  });
  if (actions.length > 8) lines.push(`…and ${actions.length - 8} more in the full report.`);
  if (review.unusualFillIns?.length) {
    lines.push('', 'Fields to confirm before signing:');
    review.unusualFillIns.forEach((field) => {
      const where = describeNdaFillInLocation(field);
      lines.push(`- "${field.label}"${where ? ` (${where})` : ''}`);
      if (field.line) lines.push(`   As printed: ${field.line}`);
    });
  }
  if (review.proposedEdits?.length) lines.push('', `Routine edits proposed: ${review.proposedEdits.length}.`);
  lines.push(
    '',
    // A raw review id means nothing to the recipient; a link opens the review itself
    // (the NDA Agent is invitation-only, and Legal's addresses are on its access list).
    jobId
      ? `Open the full review in the NDA Agent (sign-in required): ${window.location.origin}/nda-agent?review=${encodeURIComponent(jobId)}`
      : 'The full findings report can be exported from the NDA Agent.',
    '',
    'Thanks,',
  );
  const subject = `NDA for Legal review: ${fileName || counterparty || 'NDA'}`;
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
};

const NdaEscalationActionRow = ({ action, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <article className="overflow-hidden rounded-xl border border-rose-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <p className="font-bold text-gray-950">{action.title}</p>
          <p className="mt-1 text-xs font-semibold text-rose-700">
            {action.severity ? `${formatNdaSeverity(action.severity)} · Legal escalation` : 'Legal escalation'}
          </p>
        </div>
        {open ? <ChevronUp className="shrink-0 text-gray-400" size={18} /> : <ChevronDown className="shrink-0 text-gray-400" size={18} />}
      </button>
      {open && (
        <div className="space-y-4 border-t border-rose-100 px-4 py-4 text-sm leading-6 text-gray-700">
          <p>{action.description}</p>
          {action.evidence.map((evidence, index) => (
            <div key={`${action.title}-evidence-${index}`} className="rounded-lg bg-gray-50 px-4 py-3">
              <div className="mb-2 text-xs font-bold text-gray-500">{formatNdaPages(evidence.pageNumbers)}</div>
              <blockquote>“{evidence.exactQuote}”</blockquote>
            </div>
          ))}
          {action.absenceCheck?.completeDocumentReviewed && (
            <div className="flex items-center gap-2 font-semibold text-emerald-700">
              <CheckCircle2 size={16} />
              Complete document checked
            </div>
          )}
        </div>
      )}
    </article>
  );
};

// Approved escalation codes that are not policy rules (Sylvan, Oct 2026).
export const NDA_NON_CATALOG_ESCALATIONS = {
  'NDA-DOC-01': 'Not an NDA',
  'NDA-SYS-01': 'Review Integrity Failure',
};

export const buildNdaEscalationActions = (review) => {
  const triggers = review.escalationTriggers || [];
  const conflicts = review.intakeValidation?.consistent === false
    ? (review.intakeValidation.conflicts || [])
    : [];
  const conflictSet = new Set(conflicts);
  const ruleCatalog = review.ruleCatalog || [];
  const ruleResults = review.ruleResults || [];
  const ruleResult = (ruleId) => ruleResults.find((item) => item.ruleId === ruleId);
  const expectedFlow = review.intakeValidation?.expectedInformationFlow
    || review.expectedInformationFlow;
  const inferredFlow = review.intakeValidation?.inferredInformationFlow
    || review.documentAssessment?.inferredInformationFlow;
  const mutualResult = ruleResult('NDA-A-01');
  const mergeMutualObligations = Boolean(
    conflicts.length
    && expectedFlow === 'mutual'
    && inferredFlow
    && inferredFlow !== 'mutual'
    && mutualResult?.consequence === 'escalate_to_legal'
    && ['unreasonable', 'uncertain'].includes(mutualResult.status)
  );
  // Severity comes from the API's ranked escalationItems (policy v5 onward). Reviews
  // without it keep their original order and show no level.
  const severityByRule = new Map(
    (review.escalationItems || review.escalation_items || [])
      .filter((item) => item.ruleId && NDA_SEVERITY_RANK[item.severity] !== undefined)
      .map((item) => [item.ruleId, item.severity])
  );
  const highestSeverity = (...ruleIds) => ruleIds
    .map((ruleId) => severityByRule.get(ruleId))
    .filter(Boolean)
    .sort((left, right) => NDA_SEVERITY_RANK[right] - NDA_SEVERITY_RANK[left])[0] || null;
  const actions = [];

  if (conflicts.length) {
    const intakeRule = ruleCatalog.find((item) => item.id === 'NDA-DIR-01');
    const intakeResult = ruleResult('NDA-DIR-01');
    actions.push({
      title: intakeRule?.title || 'Intake and Document Consistency',
      description: conflicts.join(' '),
      evidence: deduplicateNdaEvidence([
        ...(intakeResult?.evidence || []),
        ...(mergeMutualObligations ? (mutualResult?.evidence || []) : []),
      ]),
      absenceCheck: null,
      severity: highestSeverity('NDA-DIR-01', ...(mergeMutualObligations ? ['NDA-A-01'] : [])),
    });
  }

  triggers.forEach((trigger) => {
    if (conflictSet.has(trigger) || (conflicts.length && /^NDA-DIR-01:/i.test(trigger))) return;
    const [ruleId, ...descriptionParts] = trigger.split(':');
    const normalizedRuleId = ruleId.trim();
    const hasRuleId = /^NDA-[A-Z]+-\d+$/i.test(normalizedRuleId);
    if (mergeMutualObligations && normalizedRuleId === 'NDA-A-01') return;
    const rule = hasRuleId ? ruleCatalog.find((item) => item.id === normalizedRuleId) : null;
    const result = hasRuleId ? ruleResult(normalizedRuleId) : null;
    // NDA-DOC-01 and NDA-SYS-01 are approved escalation codes outside the rule catalog;
    // their trigger reads "<id>: <title> — <reason>", so show the title and the reason.
    const systemTitle = NDA_NON_CATALOG_ESCALATIONS[normalizedRuleId];
    const detail = descriptionParts.join(':').trim();
    actions.push({
      title: rule?.title || systemTitle || (hasRuleId ? normalizedRuleId : 'Legal review required'),
      description: result?.rationale
        || (systemTitle ? detail.replace(new RegExp(`^${systemTitle}\\s*[—-]\\s*`), '') : null)
        || (hasRuleId ? detail : trigger),
      evidence: result?.evidence || [],
      absenceCheck: result?.absenceCheck || null,
      severity: hasRuleId ? highestSeverity(normalizedRuleId) : null,
    });
  });

  // Most serious first; a stable sort keeps the original order within a level, and an
  // action with no level stays after the ranked ones.
  return actions
    .map((action, index) => ({ action, index }))
    .sort((left, right) => (
      (NDA_SEVERITY_RANK[right.action.severity] ?? -1) - (NDA_SEVERITY_RANK[left.action.severity] ?? -1)
      || left.index - right.index
    ))
    .map(({ action }) => action);
};

export const buildNdaLegalReviewReasons = (review, escalationActions) => {
  if (review.disposition !== 'escalate_to_legal' || escalationActions.length > 0) return [];

  const ruleCatalog = review.ruleCatalog || [];
  const ruleResults = review.ruleResults || [];
  const reasons = [];
  const seen = new Set();

  (review.uncertainties || []).forEach((uncertainty) => {
    const rawMessage = typeof uncertainty === 'string'
      ? uncertainty
      : uncertainty?.message || uncertainty?.description || '';
    if (!rawMessage.trim()) return;

    const ruleId = (rawMessage.match(/\bNDA-[A-Z]+-\d+\b/i) || [])[0]?.toUpperCase();
    const rule = ruleId ? ruleCatalog.find((item) => item.id === ruleId) : null;
    const result = ruleId ? ruleResults.find((item) => item.ruleId === ruleId) : null;
    const description = humanizeNdaLegalReviewReason(rawMessage, rule?.title);
    const key = description.toLowerCase();
    if (!description || seen.has(key)) return;
    seen.add(key);

    reasons.push({
      title: rule?.title || ndaLegalReviewReasonTitle(rawMessage),
      description,
      evidence: result?.evidence || [],
      absenceCheck: result?.absenceCheck || null,
    });
  });

  if (reasons.length === 0) {
    reasons.push({
      title: 'Review could not be completed safely',
      description: 'The automated review could not confirm that this NDA is safe to process without Legal review.',
      evidence: [],
      absenceCheck: null,
    });
  }
  return reasons;
};

const ndaLegalReviewReasonTitle = (message) => {
  if (/extract|source page|document text/i.test(message)) return 'Document could not be fully read';
  if (/verification|verifier|coverage/i.test(message)) return 'Independent review was incomplete';
  if (/edit|anchor|approved clause/i.test(message)) return 'A proposed change could not be validated';
  if (/evidence|quote|provenance/i.test(message)) return 'Supporting evidence could not be validated';
  return 'Unresolved review uncertainty';
};

const humanizeNdaLegalReviewReason = (message, ruleTitle) => {
  const text = String(message).trim();
  if (/has no validated source evidence|validated source evidence (?:is|was) (?:missing|not found)/i.test(text)) {
    const subject = ruleTitle || 'this provision';
    return `The review could not confirm the source language needed to evaluate ${subject}. Legal should review that provision before the NDA moves forward.`;
  }
  if (/status\s+['"][^'"]+['"]/i.test(text) && ruleTitle) {
    return `The review could not reach a reliable conclusion about ${ruleTitle}. Legal should review that provision before the NDA moves forward.`;
  }
  if (/Gemini extraction did not represent every source page completely/i.test(text)) {
    return 'The document could not be read completely, so the automated review may have missed relevant language.';
  }
  if (/Long-document synthesis did not cover every evaluated segment/i.test(text)) {
    return 'The automated review could not confirm coverage of the entire document.';
  }
  if (/Independent verification coverage is incomplete|verifier did not confirm coverage/i.test(text)) {
    return 'The independent verification pass could not confirm every required policy check.';
  }
  if (/material disagreement/i.test(text)) {
    return 'The primary review and independent verification reached a material disagreement that requires Legal judgment.';
  }
  if (/^Verifier objection:/i.test(text)) {
    return `The independent verification raised an unresolved concern: ${cleanNdaInternalLanguage(text.replace(/^Verifier objection:\s*/i, ''))}`;
  }
  if (/\bedit-[\w-]+\s*:/i.test(text)) {
    return `A proposed change could not be validated safely: ${cleanNdaInternalLanguage(text.replace(/^\s*edit-[\w-]+\s*:\s*/i, ''))}`;
  }
  if (/^NDA-[A-Z]+-\d+\s*:/i.test(text)) {
    const detail = cleanNdaInternalLanguage(text.replace(/^NDA-[A-Z]+-\d+\s*:\s*/i, ''));
    return ruleTitle ? `${ruleTitle} could not be resolved automatically. ${detail}` : detail;
  }
  return cleanNdaInternalLanguage(text);
};

const cleanNdaInternalLanguage = (message) => String(message)
  .replace(/\b(?:Gemini|Claude)\b/gi, 'AI')
  .replace(/\b(?:p\d+-b\d+|ru-\d+)\b/gi, '')
  .replace(/\s*\([^)]*(?:blocks?|review units?|provenance)[^)]*\)/gi, '')
  .replace(/\s{2,}/g, ' ')
  .replace(/\s+([.,;:])/g, '$1')
  .trim();

const deduplicateNdaEvidence = (items = []) => {
  const seen = new Set();
  return items.filter((evidence) => {
    const key = `${evidence.exactQuote || ''}|${(evidence.pageNumbers || []).join(',')}`;
    if (!evidence.exactQuote || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const NdaEditCard = ({ edit, ruleCatalog, severity = null, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const rule = ruleCatalog.find((item) => item.id === edit.authorizationRuleId);
  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <div>
          <p className="font-bold text-gray-950">{rule?.title || edit.authorizationRuleId || 'Routine correction'}</p>
          <p className="mt-0.5 text-xs font-semibold text-amber-800">
            {severity ? `${formatNdaSeverity(severity)} · ${formatNdaOperation(edit.operation)}` : formatNdaOperation(edit.operation)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {edit.anchor?.pageNumber && (
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">Page {edit.anchor.pageNumber}</span>
          )}
          {open ? <ChevronUp className="text-gray-400" size={18} /> : <ChevronDown className="text-gray-400" size={18} />}
        </div>
      </button>
      {open && (
        <div>
          <div className="border-t border-gray-200 px-4 py-4 sm:px-5">
            <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-600">
              <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
              Clause
              {edit.anchor?.pageNumber && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">
                  Page {edit.anchor.pageNumber}
                </span>
              )}
            </p>
            <blockquote className="whitespace-pre-wrap border-l-2 border-gray-300 pl-3 text-sm italic leading-6 text-gray-600">
              {edit.anchor?.exactAnchorQuote || 'No exact source clause was returned for this edit.'}
            </blockquote>
          </div>
          <div className="border-t border-orange-100 bg-orange-50/40 px-4 py-4 sm:px-5">
            <div className="flex gap-3">
              <span className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sylvan-600">
                <Sparkles size={16} />
              </span>
              <div className="min-w-0 flex-1 text-sm leading-6 text-gray-700">
                <p><span className="font-bold text-sylvan-700">AI Summary — </span>{edit.rationale}</p>
                {edit.approvedText && (
                  <div className="mt-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-900">Recommended replacement</p>
                    <div className="mt-1.5 rounded-lg border border-orange-200 bg-white px-3.5 py-3 text-gray-800">
                      {formatNdaClauseLines(edit.approvedText).map((line, index) => (
                        <p key={index} className={`${index > 0 ? 'mt-1.5' : ''}${isNdaEnumeratedLine(line) ? ' pl-5' : ''}`}>
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

const NDA_ADVISORY_SIGNAL_PATTERN = /\b(missing|narrow(?:ed|er)?|overbroad|overly broad|restrictive|burdensome|onerous|one-sided|anti-mosaic|retention|backup|purge|termination|assignment|derivative works?|pre-?identif|case-by-case consent|affiliates?|subcontractors?|consultants?|own counsel|disclos(?:er|ing party).{0,30}counsel|may warrant|could be read)\b/i;
const NDA_ADVISORY_PROCESS_PATTERN = /\b(extraction sequencing|formatting\/extraction|review units?|classified as residual|all \d+ manifest rules|no material disagreements?|no corrections were required|preliminary disposition|classification is supported|correctly classified|no status change is warranted|no correction is needed|noted for completeness|this objection is informational)\b/i;

export const buildNdaAdvisoryConcerns = (review) => {
  const ruleCatalog = review.ruleCatalog || [];
  const ruleResults = review.ruleResults || [];
  const concerns = [];
  const seen = new Set();

  (review.warnings || []).forEach((warning) => {
    if (typeof warning !== 'string' || !NDA_ADVISORY_SIGNAL_PATTERN.test(warning)) return;

    const description = humanizeNdaAdvisoryText(warning, ruleCatalog);
    if (!description || NDA_ADVISORY_PROCESS_PATTERN.test(description)) return;

    const ruleIds = [...new Set(warning.match(/\bNDA-[A-Z]+-\d+\b/g) || [])];
    const relatedRules = ruleIds
      .map((ruleId) => ruleResults.find((result) => result.ruleId === ruleId))
      .filter(Boolean);
    const title = ndaAdvisoryTitle(warning, ruleIds, ruleCatalog);
    const key = title.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    concerns.push({
      title,
      description,
      evidence: deduplicateNdaEvidence(relatedRules.flatMap((result) => result.evidence || [])),
    });
  });

  return concerns;
};

const humanizeNdaAdvisoryText = (warning, ruleCatalog) => {
  let text = warning
    .replace(/^Verifier note:\s*/i, '')
    .replace(/^Non-material\s+(?:administrative|informational|verification)?\s*note:\s*/i, '')
    .replace(/^\s*NDA-[A-Z]+-\d+\s*:\s*/i, '')
    .replace(/\s*\([^)]*(?:blocks?|review units?|ru-\d+|p\d+-b\d+)[^)]*\)/gi, '')
    .replace(/\bNDA-[A-Z]+-\d+\b/g, (ruleId) => (
      ruleCatalog.find((rule) => rule.id === ruleId)?.title || ruleId
    ))
    .replace(/\s+/g, ' ')
    .trim();

  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
  const humanSentences = sentences.filter((sentence) => !NDA_ADVISORY_PROCESS_PATTERN.test(sentence));
  text = (humanSentences.length ? humanSentences : sentences.slice(0, 1)).join(' ').trim();

  if (!text) return '';
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

const ndaAdvisoryTitle = (warning, ruleIds, ruleCatalog) => {
  const value = warning.toLowerCase();
  if (value.includes('exclusion') || value.includes('anti-mosaic')) return 'Narrow confidentiality exclusions';
  if (value.includes('compelled disclosure') || value.includes('own counsel')) return 'Restrictive compelled-disclosure process';
  if (value.includes('backup') || value.includes('retention') || value.includes('purge')) return 'Limited document-retention rights';
  if (value.includes('termination')) return 'One-sided termination rights';
  if (value.includes('affiliate') || value.includes('subcontractor') || value.includes('consultant') || value.includes('pre-identif')) {
    return 'Restricted sharing with project participants';
  }
  if (value.includes('assignment') || value.includes('derivative work') || value.includes('modifications or additions')) {
    return 'Potential ownership claim over derivative work';
  }
  if (value.includes('overbroad') || value.includes('overly broad')) return 'Broad definition of confidential information';
  const matchedRule = ruleIds
    .map((ruleId) => ruleCatalog.find((rule) => rule.id === ruleId))
    .find(Boolean);
  return matchedRule?.title ? `${matchedRule.title} concern` : 'Additional negotiation concern';
};

const NdaAdvisoryConcernRow = ({ concern }) => {
  const [open, setOpen] = useState(false);
  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
            <Lightbulb size={16} />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-gray-950">{concern.title}</p>
            <p className="mt-1 text-xs font-semibold text-gray-600">Consider during negotiation</p>
          </div>
        </div>
        {open ? <ChevronUp className="shrink-0 text-gray-400" size={18} /> : <ChevronDown className="shrink-0 text-gray-400" size={18} />}
      </button>
      {open && (
        <div className="space-y-4 border-t border-gray-100 px-4 py-4 text-sm leading-6 text-gray-700">
          <p>{concern.description}</p>
          {concern.evidence.map((evidence, index) => (
            <div key={`${concern.title}-evidence-${index}`} className="rounded-lg bg-gray-50 px-4 py-3">
              <div className="mb-2 text-xs font-bold text-gray-500">{formatNdaPages(evidence.pageNumbers)}</div>
              <blockquote>“{evidence.exactQuote}”</blockquote>
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

const NDA_CHECKLIST_GROUPS = [
  ['required_provision', 'Required Provisions'],
  ['routine_edit_authorization', 'Routine Corrections'],
  ['mandatory_escalation', 'Mandatory Escalations'],
  ['directionality', 'Directionality'],
];

const NdaChecklist = ({ ruleResults, ruleCatalog }) => {
  let renderedRuleIndex = 0;
  return (
    <div className="space-y-6">
      {NDA_CHECKLIST_GROUPS.map(([category, label]) => {
        const rules = ruleResults.filter((result) => (
          ruleCatalog.find((item) => item.id === result.ruleId)?.category === category
        ));
        if (!rules.length) return null;
        return (
          <section key={category}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-base font-bold text-gray-950">{label}</h2>
              <span className="text-xs font-semibold text-gray-500">{rules.length} checks</span>
            </div>
            <div className="space-y-3">
              {rules.map((result) => {
                const defaultOpen = renderedRuleIndex === 0;
                renderedRuleIndex += 1;
                return (
                  <NdaChecklistRow
                    key={result.ruleId}
                    result={result}
                    rule={ruleCatalog.find((item) => item.id === result.ruleId)}
                    defaultOpen={defaultOpen}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

const NdaChecklistRow = ({ result, rule, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const status = ndaRuleStatusDisplay(result);
  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <p className="font-bold text-gray-950">{rule?.title || result.ruleId}</p>
          <p className="mt-1 text-xs font-semibold text-gray-500">{result.ruleId}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${status.className}`}>{status.label}</span>
          {open ? <ChevronUp className="text-gray-400" size={18} /> : <ChevronDown className="text-gray-400" size={18} />}
        </div>
      </button>
      {open && (
        <div className="space-y-4 border-t border-gray-100 px-4 py-4 text-sm leading-6">
          <p className="text-gray-700">{result.rationale}</p>
          {result.evidence.map((evidence, index) => (
            <div key={`${result.ruleId}-evidence-${index}`} className="rounded-lg bg-gray-50 px-4 py-3">
              <div className="mb-2 text-xs font-bold text-gray-500">{formatNdaPages(evidence.pageNumbers)}</div>
              <blockquote className="text-gray-700">“{evidence.exactQuote}”</blockquote>
            </div>
          ))}
          {result.absenceCheck?.completeDocumentReviewed && (
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
              <CheckCircle2 size={16} />
              Complete document checked
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const ComparisonWorkspace = ({ summary, job, baseFile, revisedFile, onPromptClick }) => {
  const [activeView, setActiveView] = useState('summary');
  const differences = summary?.differences || [];
  const tableRows = comparisonSummaryRows(differences);
  const highCount = differences.filter((difference) => comparisonRiskLevel(difference) === 'high').length;
  const mediumCount = differences.filter((difference) => comparisonRiskLevel(difference) === 'medium').length;
  const lowCount = differences.filter((difference) => comparisonRiskLevel(difference) === 'low').length;
  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
        <button type="button" onClick={() => setActiveView('summary')} className="min-w-0">
          <WorkspaceTab icon={<MessageSquare size={16} />} active={activeView === 'summary'} label="Summary" />
        </button>
        <button type="button" onClick={() => setActiveView('clauses')} className="min-w-0">
          <WorkspaceTab icon={<FileText size={16} />} active={activeView === 'clauses'} label="Clause-by-Clause" badge={`${differences.length} diff`} />
        </button>
      </div>
      {job?.agreement_type_warning && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          <AlertTriangle className="mt-0.5 shrink-0" size={18} />
          <div>
            <p className="font-semibold">Contract type may not match</p>
            <p className="mt-1 leading-5">{job.agreement_type_warning}</p>
          </div>
        </div>
      )}
      {activeView === 'summary' && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            <WorkspaceMetric title="Terms Compared" value={differences.length || 0} tone="orange" />
            <WorkspaceMetric title="High Risk" value={highCount} tone="red" />
            <WorkspaceMetric title="Medium Risk" value={mediumCount} tone="amber" />
            <WorkspaceMetric title="Low Risk" value={lowCount} tone="green" />
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
                <Sparkles size={19} />
              </span>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-950">AI Recommendation</p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  {summary?.summary || job?.error || job?.progress_message || `Comparing ${baseFile?.name || 'Contract A'} and ${revisedFile?.name || 'Contract B'}.`}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
      {activeView === 'summary' ? (
        <ComparisonSummaryTable
          rows={tableRows}
          baseName={baseFile?.name || 'Contract A'}
          revisedName={revisedFile?.name || 'Contract B'}
        />
      ) : (
        <ComparisonClauseCards differences={differences} baseName={baseFile?.name || 'Contract A'} revisedName={revisedFile?.name || 'Contract B'} />
      )}
    </div>
  );
};

const ComparisonSummaryTable = ({ rows, baseName, revisedName }) => (
  <>
    <div className="space-y-3 min-[1400px]:hidden">
      {rows.map((row) => (
        <div key={row.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-[#fbfaf8] px-4 py-3">
            <h3 className="min-w-0 break-words text-sm font-bold text-gray-950">{row.term}</h3>
            <RiskBadge risk={row.risk} />
          </div>
          <dl className="divide-y divide-gray-100 text-sm leading-6">
            <div className="px-4 py-3">
              <dt className="mb-1 break-words text-xs font-bold uppercase tracking-wide text-blue-600">Contract A — {shortContractName(baseName)}</dt>
              <dd className="break-words text-gray-700">{row.contractA}</dd>
            </div>
            <div className="px-4 py-3">
              <dt className="mb-1 break-words text-xs font-bold uppercase tracking-wide text-purple-600">Contract B — {shortContractName(revisedName)}</dt>
              <dd className="break-words font-semibold text-gray-900">{row.contractB}</dd>
            </div>
          </dl>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500 shadow-sm">No difference rows were returned for this comparison.</div>
      )}
    </div>

    <div className="hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm min-[1400px]:block">
      <div className="grid grid-cols-[1.15fr_1.55fr_1.55fr_0.75fr] gap-4 border-b border-gray-200 bg-[#fbfaf8] px-5 py-4 text-xs font-bold uppercase tracking-wide text-gray-500">
        <div>Term</div>
        <div>Contract A — {shortContractName(baseName)}</div>
        <div>Contract B — {shortContractName(revisedName)}</div>
        <div>Risk</div>
      </div>
      <div className="divide-y divide-gray-200">
        {rows.map((row) => (
          <div key={row.id} className="grid grid-cols-[1.15fr_1.55fr_1.55fr_0.75fr] gap-4 px-5 py-4 text-sm leading-6">
            <div className="font-bold text-gray-950">{row.term}</div>
            <div className="text-gray-700">{row.contractA}</div>
            <div className="font-semibold text-gray-900">{row.contractB}</div>
            <div><RiskBadge risk={row.risk} /></div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-5 py-8 text-sm text-gray-500">No difference rows were returned for this comparison.</div>
        )}
      </div>
    </div>
  </>
);

const ComparisonClauseCards = ({ differences, baseName, revisedName }) => (
  <div className="w-full space-y-4">
    {differences.map((difference, index) => (
      <ComparisonClauseCard key={difference.difference_id || index} difference={difference} baseName={baseName} revisedName={revisedName} defaultOpen={index === 0} />
    ))}
    {differences.length === 0 && (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm">No clause-level differences were returned for this comparison.</div>
    )}
  </div>
);

const ComparisonClauseCard = ({ difference, baseName, revisedName, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const risk = comparisonRiskLevel(difference);
  return (
    <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <button type="button" onClick={() => setOpen((current) => !current)} className="flex w-full flex-col items-stretch gap-3 px-4 py-4 text-left sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <h3 className="min-w-0 break-words text-base font-bold text-gray-950">{comparisonTerm(difference)}</h3>
          <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">{formatChangeType(difference.change_type)}</span>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
          <RiskBadge risk={risk} />
          <span className="text-gray-400">{open ? '⌃' : '⌄'}</span>
        </div>
      </button>
      {open && (
        <div>
          <div className="grid border-t border-gray-200 md:grid-cols-2">
            <div className="border-b border-gray-200 p-4 sm:p-5 md:border-b-0 md:border-r">
              <p className="mb-3 flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-600">
                <span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Contract A — {shortContractName(baseName)}
                {difference.base?.page_start && (
                  <span className="whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">
                    {formatPageRange(difference.base.page_start, difference.base.page_end)}
                  </span>
                )}
              </p>
              <p className="whitespace-pre-wrap text-sm leading-7 text-gray-700">{difference.base_quote || 'Not specified in the returned comparison.'}</p>
            </div>
            <div className="p-4 sm:p-5">
              <p className="mb-3 flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wide text-purple-600">
                <span className="h-2.5 w-2.5 rounded-full bg-purple-500" /> Contract B — {shortContractName(revisedName)}
                {difference.revised?.page_start && (
                  <span className="whitespace-nowrap rounded-full bg-purple-50 px-2 py-0.5 text-[10px] text-purple-700">
                    {formatPageRange(difference.revised.page_start, difference.revised.page_end)}
                  </span>
                )}
              </p>
              <p className="whitespace-pre-wrap text-sm font-semibold leading-7 text-gray-800">{difference.revised_quote || 'Not specified in the returned comparison.'}</p>
            </div>
          </div>
          <div className="border-t border-orange-100 bg-orange-50/40 px-4 py-4 sm:px-5">
            <div className="flex gap-3">
              <span className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sylvan-600">
                <Sparkles size={16} />
              </span>
              <p className="text-sm leading-7 text-gray-700"><span className="font-bold text-sylvan-700">AI Analysis — </span>{difference.practical_impact || difference.summary || 'No practical-impact summary was returned.'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const WorkspaceTab = ({ icon, label, active = false, badge }) => (
  <div className={`inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-t-xl border px-3 py-3 text-sm font-bold sm:w-auto sm:rounded-t-2xl sm:px-5 ${active ? 'border-gray-200 bg-white text-gray-950 shadow-sm' : 'border-transparent text-gray-500'}`}>
    {icon}
    <span>{label}</span>
    {badge && <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-bold text-amber-700">{badge}</span>}
  </div>
);

const WorkspaceMetric = ({ title, value, tone }) => {
  const toneClass = {
    red: 'text-rose-600',
    amber: 'text-amber-700',
    green: 'text-emerald-700',
    orange: 'text-sylvan-700',
  }[tone] || 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:rounded-2xl sm:p-5">
      <p className="text-sm font-semibold text-gray-500">{title}</p>
      <p className={`mt-2 text-2xl font-bold sm:mt-4 sm:text-3xl ${toneClass}`}>{value}</p>
    </div>
  );
};

const AssistantSessionPills = ({
  contractJob,
  comparisonSummary,
  onEndContract,
  onEndComparison,
}) => (
  <>
    {contractJob?.status === 'succeeded' && (
      <div className="mb-3 flex items-center justify-between rounded-xl border border-sylvan-100 bg-sylvan-50 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2 text-xs text-sylvan-800">
          <FileText size={14} className="shrink-0" />
          <span className="truncate">Contract active</span>
        </div>
        <button type="button" onClick={onEndContract} className="ml-2 rounded-md px-2 py-1 text-xs font-medium text-sylvan-700 hover:bg-white">
          End
        </button>
      </div>
    )}
    {comparisonSummary && (
      <div className="mb-3 flex items-center justify-between rounded-xl border border-sylvan-100 bg-sylvan-50 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2 text-xs text-sylvan-800">
          <GitCompare size={14} className="shrink-0" />
          <span className="truncate">Comparison active</span>
        </div>
        <button type="button" onClick={onEndComparison} className="ml-2 rounded-md px-2 py-1 text-xs font-medium text-sylvan-700 hover:bg-white">
          End
        </button>
      </div>
    )}
  </>
);

const UploadModeChoiceModal = ({
  onSelectContract,
  onSelectComparison,
  onClose,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-3 sm:px-4" onClick={onClose}>
    <div
      className="max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <UploadCloud className="text-sylvan-500" size={18} />
            <h2 className="text-base font-semibold text-gray-900">Upload contracts</h2>
          </div>
          <p className="mt-1 text-sm text-gray-600">Choose how you want to use uploaded contract files.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>
      <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={onSelectContract}
          className="min-h-[132px] rounded-lg border border-gray-200 bg-gray-50 p-4 text-left hover:border-sylvan-300 hover:bg-sylvan-50"
        >
          <FileText className="mb-3 text-sylvan-500" size={20} />
          <p className="text-sm font-semibold text-gray-900">Ask about one contract</p>
          <p className="mt-1 text-xs leading-5 text-gray-600">Upload one contract, then ask questions about obligations, risks, or clauses.</p>
        </button>
        <button
          type="button"
          onClick={onSelectComparison}
          className="min-h-[132px] rounded-lg border border-gray-200 bg-gray-50 p-4 text-left hover:border-sylvan-300 hover:bg-sylvan-50"
        >
          <GitCompare className="mb-3 text-sylvan-500" size={20} />
          <p className="text-sm font-semibold text-gray-900">Compare two contracts</p>
          <p className="mt-1 text-xs leading-5 text-gray-600">Upload Contract A and Contract B for comparison.</p>
        </button>
      </div>
    </div>
  </div>
);

const ContractUploadModal = ({
  contractFile,
  agreementType,
  agreementTypeDetail,
  loading,
  onContractFileChange,
  onAgreementTypeChange,
  onAgreementTypeDetailChange,
  onBack,
  onClose,
  onStart,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-3 sm:px-4" onClick={onClose}>
    <div
      className="max-h-[calc(100dvh-1.5rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="text-sylvan-500" size={18} />
            <h2 className="text-base font-semibold text-gray-900">Ask about one contract</h2>
          </div>
          <p className="mt-1 text-sm text-gray-600">Upload one PDF or DOCX and I’ll prepare it for Q&A.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>
      <div className="space-y-3 px-5 py-4">
        <FilePicker label="Contract for Q&A" file={contractFile} onChange={(file) => onContractFileChange(file)} />
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-gray-800">Contract Type <span className="text-red-600">*</span></span>
          <select
            value={agreementType}
            onChange={(event) => onAgreementTypeChange(event.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sylvan-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
            required
          >
            <option value="" disabled>Select Contract Type</option>
            {AGREEMENT_TYPE_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        {agreementType === 'other' && (
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-gray-800">Describe Contract Type <span className="font-normal text-gray-500">(optional)</span></span>
            <input
              type="text"
              value={agreementTypeDetail}
              maxLength={200}
              onChange={(event) => onAgreementTypeDetailChange(event.target.value)}
              placeholder="Describe the agreement"
              className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-sylvan-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
            />
          </label>
        )}
      </div>
      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onStart}
          disabled={loading || !contractFile || !agreementType}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-sylvan-500 px-4 py-2 text-sm font-medium text-white hover:bg-sylvan-600 disabled:opacity-50 sm:w-auto"
        >
          {loading ? <Loader2 className="mr-2 animate-spin" size={16} /> : <UploadCloud className="mr-2" size={16} />}
          {loading ? 'Uploading' : 'Start Clause Search'}
        </button>
      </div>
    </div>
  </div>
);

const ComparisonUploadModal = ({
  baseFile,
  revisedFile,
  agreementType,
  agreementTypeDetail,
  loading,
  onBaseFileChange,
  onRevisedFileChange,
  onAgreementTypeChange,
  onAgreementTypeDetailChange,
  onBack,
  onClose,
  onStart,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-3 sm:px-4" onClick={onClose}>
    <div
      className="max-h-[calc(100dvh-1.5rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <GitCompare className="text-sylvan-500" size={18} />
            <h2 className="text-base font-semibold text-gray-900">Compare two contracts</h2>
          </div>
          <p className="mt-1 text-sm text-gray-600">Upload both contracts and I’ll summarize the differences in chat.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>
      <div className="space-y-3 px-5 py-4">
        <FilePicker label="Contract A" file={baseFile} onChange={(file) => onBaseFileChange(file)} />
        <FilePicker label="Contract B" file={revisedFile} onChange={(file) => onRevisedFileChange(file)} />
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-gray-800">Contract Type <span className="text-red-600">*</span></span>
          <select
            value={agreementType}
            onChange={(event) => onAgreementTypeChange(event.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sylvan-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
            required
          >
            <option value="" disabled>Select Contract Type</option>
            {AGREEMENT_TYPE_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        {agreementType === 'other' && (
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-gray-800">Describe Contract Type <span className="font-normal text-gray-500">(optional)</span></span>
            <input
              type="text"
              value={agreementTypeDetail}
              maxLength={200}
              onChange={(event) => onAgreementTypeDetailChange(event.target.value)}
              placeholder="Describe the agreement"
              className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-sylvan-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
            />
          </label>
        )}
      </div>
      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onStart}
          disabled={loading || !baseFile || !revisedFile || !agreementType}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-sylvan-500 px-4 py-2 text-sm font-medium text-white hover:bg-sylvan-600 disabled:opacity-50 sm:w-auto"
        >
          {loading ? <Loader2 className="mr-2 animate-spin" size={16} /> : <UploadCloud className="mr-2" size={16} />}
          {loading ? 'Uploading' : 'Start comparison'}
        </button>
      </div>
    </div>
  </div>
);

const FilePicker = ({ label, file, onChange }) => {
  const inputId = useId();
  const inputRef = useRef(null);
  const openFilePicker = () => inputRef.current?.click();

  const handleFileChange = (event) => {
    onChange(event.target.files?.[0] || null);
    event.target.value = '';
  };

  const clearFile = (event) => {
    event.stopPropagation();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openFilePicker}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openFilePicker();
        }
      }}
      className="min-h-[68px] cursor-pointer rounded-lg border border-gray-200 bg-white p-3 text-sm hover:border-sylvan-300 focus:outline-none focus:ring-2 focus:ring-sylvan-200"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 flex min-h-[24px] items-center justify-between gap-2">
        <span className="min-w-0 flex-1 truncate text-xs text-gray-700">
          {file ? file.name : 'PDF or DOCX'}
        </span>
        {file && (
          <button
            type="button"
            onClick={clearFile}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label={`Remove ${label}`}
          >
            <X size={14} />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleFileChange}
        className="sr-only"
      />
    </div>
  );
};

const COMPARISON_FOLLOW_UP_PROMPTS = [
  'Which changes create the most risk?',
  'What changed in payment terms?',
  'What changed in indemnification?',
  'Which version is more favorable?',
  'Summarize the added obligations.',
];

const CONTRACT_FOLLOW_UP_PROMPTS = [
  'What are the highest risk clauses?',
  'What are the payment obligations?',
  'What should I negotiate first?',
  'Show me historical clauses similar to this contract’s indemnity clause.',
  'Summarize the insurance requirements.',
];

const ChatMessage = ({
  message,
  onPromptClick,
  onRetryContractReview,
  onRetryComparison,
  contractLoading,
  comparisonLoading,
}) => {
  const isUser = message.role === 'user';
  const [typingComplete, setTypingComplete] = useState(!message.typing);

  useEffect(() => {
    setTypingComplete(!message.typing);
  }, [message.id, message.text, message.typing]);

  const showTypewriter = !isUser && message.typing && message.kind !== 'error';
  const showPayload = message.payload && (!message.typing || typingComplete);

  return (
    <div className={`flex w-full min-w-0 ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`w-full min-w-0 sm:max-w-[92%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-sylvan-100 text-sylvan-700">
            {isUser ? <User size={15} /> : <Bot size={15} />}
          </span>
          <span className="text-xs font-medium text-gray-500">{isUser ? 'You' : 'Clause Search'}</span>
        </div>
        <div
          className={`max-w-full overflow-hidden rounded-lg border p-4 ${
            isUser ? 'bg-sylvan-50 text-gray-800 border-sylvan-200' : 'bg-white text-gray-800 border-gray-200'
          }`}
        >
          {message.kind === 'error' ? (
            <div className="flex text-red-700">
              <AlertTriangle className="mr-2 mt-0.5 shrink-0" size={16} />
              <p className="text-sm">{message.text}</p>
            </div>
          ) : message.kind === 'comparison' ? (
            <ComparisonMessageContent
              message={message}
              onPromptClick={onPromptClick}
              onRetry={onRetryComparison}
              retryDisabled={comparisonLoading}
            />
          ) : message.kind === 'contract-review' ? (
            <ContractReviewMessageContent
              message={message}
              onRetry={onRetryContractReview}
              retryDisabled={contractLoading}
            />
          ) : showTypewriter ? (
            <TypewriterText text={message.text} onDone={() => setTypingComplete(true)} />
          ) : (
            <>
              {isUser ? (
                <p className="break-words text-sm leading-6 whitespace-pre-wrap text-gray-700">{message.text}</p>
              ) : (
                <MarkdownChatContent className="min-w-0 [overflow-wrap:anywhere]">{message.text}</MarkdownChatContent>
              )}
              {message.cited_difference_ids?.length > 0 && (
                <p className="mt-2 text-xs text-gray-500">Cites {message.cited_difference_ids.join(', ')}</p>
              )}
              {message.cited_unit_ids?.length > 0 && (
                <p className="mt-2 text-xs text-gray-500">Cites units {message.cited_unit_ids.join(', ')}</p>
              )}
              {message.cited_finding_ids?.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">Cites findings {message.cited_finding_ids.join(', ')}</p>
              )}
            </>
          )}
          {showPayload && <ChatResults payload={message.payload} onPromptClick={onPromptClick} />}
        </div>
      </div>
    </div>
  );
};

const ContractReviewMessageContent = ({ message, onRetry, retryDisabled }) => {
  const progress = comparisonProgress(message.job);
  const failed = message.job?.status === 'failed';
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-start gap-2">
        {message.job?.status === 'succeeded' ? (
          <CheckCircle2 className="mt-0.5 shrink-0 text-green-600" size={16} />
        ) : failed ? (
          <AlertTriangle className="mt-0.5 shrink-0 text-red-600" size={16} />
        ) : (
          <Loader2 className="mt-0.5 shrink-0 animate-spin text-sylvan-500" size={16} />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">{message.text}</p>
          <p className={`mt-1 text-xs ${failed ? 'text-red-700' : 'text-gray-600'}`}>
            {message.job?.error || message.job?.progress_message || formatJobStatus(message.job)}
          </p>
        </div>
      </div>
      <div>
        <div className="h-2 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full transition-all duration-500 ${failed ? 'bg-red-500' : 'bg-sylvan-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-gray-500">
          <span>{formatJobStatus(message.job)}</span>
          <span>{progress}%</span>
        </div>
      </div>
      {failed && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retryDisabled}
          className="inline-flex items-center rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className="mr-1.5" size={13} />
          Retry
        </button>
      )}
    </div>
  );
};

const ContractFindingsSummary = ({ findings, findingCount }) => {
  return (
    <div className="space-y-4">
      {findings.map((finding, index) => (
        <ContractFindingCard key={finding.finding_id || index} finding={finding} defaultOpen={index === 0} />
      ))}
      {findingCount === 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm">
          No accepted risk findings were generated for this contract.
        </div>
      )}
    </div>
  );
};

const ContractFindingCard = ({ finding, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const title = compactFindingTitle(finding);
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <button type="button" onClick={() => setOpen((current) => !current)} className="flex w-full flex-col items-stretch gap-3 px-4 py-4 text-left sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3">
          <h3 className="min-w-0 break-words text-base font-bold text-gray-950 sm:truncate" title={finding.issue_summary || title}>
            {title}
          </h3>
          {finding.quote_page_start && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-600">
              {formatPageRange(finding.quote_page_start, finding.quote_page_end || finding.quote_page_start)}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
          <RiskBadge risk={finding.risk_level} />
          <span className="text-gray-400">{open ? '⌃' : '⌄'}</span>
        </div>
      </button>
      {open && (
        <div>
          <div className="border-t border-gray-200 px-4 py-4 sm:px-5">
            <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-600">
              <span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Clause
              {finding.quote_page_start && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">
                  {formatPageRange(finding.quote_page_start, finding.quote_page_end || finding.quote_page_start)}
                </span>
              )}
            </p>
            <p className="whitespace-pre-wrap text-sm leading-7 text-gray-700">
              {finding.exact_quote || 'No exact quote was returned for this finding.'}
            </p>
          </div>
          <div className="border-t border-orange-100 bg-orange-50/40 px-4 py-4 sm:px-5">
            <div className="flex gap-3">
              <span className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sylvan-600">
                <Sparkles size={16} />
              </span>
              <div className="min-w-0 text-sm leading-7 text-gray-700">
                <p><span className="font-bold text-sylvan-700">AI Summary — </span>{finding.explanation || finding.issue_summary || 'No explanation was returned.'}</p>
                {finding.suggested_edit && <p className="mt-2"><span className="font-semibold text-gray-900">Suggested edit: </span>{finding.suggested_edit}</p>}
                {finding.fallback_position && <p className="mt-1"><span className="font-semibold text-gray-900">Fallback: </span>{finding.fallback_position}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ComparisonMessageContent = ({ message, onPromptClick, onRetry, retryDisabled }) => {
  const progress = comparisonProgress(message.job);
  const failed = message.job?.status === 'failed';
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-start gap-2">
        {message.job?.status === 'succeeded' ? (
          <CheckCircle2 className="mt-0.5 shrink-0 text-green-600" size={16} />
        ) : failed ? (
          <AlertTriangle className="mt-0.5 shrink-0 text-red-600" size={16} />
        ) : (
          <Loader2 className="mt-0.5 shrink-0 animate-spin text-sylvan-500" size={16} />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">{message.text}</p>
          <p className={`mt-1 text-xs ${failed ? 'text-red-700' : 'text-gray-600'}`}>
            {message.job?.error || message.job?.progress_message || formatJobStatus(message.job)}
          </p>
        </div>
      </div>
      <div>
        <div className="h-2 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full transition-all duration-500 ${failed ? 'bg-red-500' : 'bg-sylvan-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-gray-500">
          <span>{formatJobStatus(message.job)}</span>
          <span>{progress}%</span>
        </div>
      </div>
      {failed && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retryDisabled}
          className="inline-flex items-center rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className="mr-1.5" size={13} />
          Retry
        </button>
      )}
    </div>
  );
};

const TypewriterText = ({ text, onDone }) => {
  const [visibleText, setVisibleText] = useState('');
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    setVisibleText('');
    if (!text) {
      onDoneRef.current?.();
      return undefined;
    }
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setVisibleText(text.slice(0, index));
      if (index >= text.length) {
        window.clearInterval(timer);
        onDoneRef.current?.();
      }
    }, TYPEWRITER_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [text]);

  return (
    <div className="relative">
      <MarkdownChatContent className="min-w-0 [overflow-wrap:anywhere]">{visibleText}</MarkdownChatContent>
      {visibleText.length < text.length && <span className="ml-0.5 animate-pulse text-sylvan-500">|</span>}
    </div>
  );
};

const ChatResults = ({ payload, onPromptClick }) => (
  <ClauseResults payload={payload} showIntent onPromptClick={onPromptClick} />
);

const ClauseResults = ({ payload, showIntent = false, onPromptClick }) => {
  const [selectedClause, setSelectedClause] = useState(null);
  const showSimilarityScore = payload.mode === 'search';
  const groupResultsByClauseType = payload.intent?.intent === 'browse_clause_types';
  const groupedMatches = groupResultsByClauseType ? groupMatchesByClauseType(payload.matches || []) : [];

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setSelectedClause(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="mt-4 space-y-3">
      {showIntent && payload.intent && (
        <div className="rounded-lg bg-sylvan-50 border border-sylvan-100 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-sylvan-700">Understood as</p>
          <p className="text-sm text-gray-800 mt-1">
            {formatIntent(payload.intent)}
            <span className="text-xs text-gray-500 ml-2">{formatPercent(payload.intent.confidence)} confidence</span>
          </p>
        </div>
      )}
      {payload.classification && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Detected clause type</p>
          <p className="text-sm text-gray-800 mt-1">
            {formatClauseType(payload.classification.clause_type)}
            <span className="text-xs text-gray-500 ml-2">
              {formatPercent(payload.classification.confidence)} confidence
            </span>
          </p>
          <p className="text-xs text-gray-600 mt-1">{payload.classification.rationale}</p>
        </div>
      )}
      {payload.clause_types?.length > 0 && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Supported clause types</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {payload.clause_types.map((clauseType) => (
              <span
                key={clauseType}
                className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700"
              >
                {formatClauseType(clauseType)}
              </span>
            ))}
          </div>
        </div>
      )}
      {payload.sample_questions?.length > 0 && (
        <div className="rounded-lg bg-white border border-gray-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sample questions</p>
          <div className="mt-2 space-y-1">
            {payload.sample_questions.map((question) => (
              <button
                key={question}
                type="button"
                onClick={() => onPromptClick?.(question)}
                className="block w-full rounded-md px-2 py-1 text-left text-xs text-gray-700 leading-5 hover:bg-sylvan-50 hover:text-sylvan-700"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}
      {groupResultsByClauseType
        ? groupedMatches.map((group) => (
            <div key={group.clauseType || 'unknown'} className="space-y-2">
              <div className="flex items-center gap-2 pt-1">
                <h3 className="text-sm font-semibold text-gray-900">{formatClauseType(group.clauseType)}</h3>
                <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-500">
                  {group.matches.length}
                </span>
              </div>
              {group.matches.map((match, index) => (
                <ClauseMatchCard
                  key={`${match.contract_id}:${match.unit_id}`}
                  match={match}
                  index={index}
                  showSimilarityScore={showSimilarityScore}
                  onSelect={setSelectedClause}
                />
              ))}
            </div>
          ))
        : (payload.matches || []).map((match, index) => (
            <ClauseMatchCard
              key={`${match.contract_id}:${match.unit_id}`}
              match={match}
              index={index}
              showSimilarityScore={showSimilarityScore}
              onSelect={setSelectedClause}
            />
          ))}
      {selectedClause && <FullClauseModal match={selectedClause} onClose={() => setSelectedClause(null)} />}
    </div>
  );
};

const ClauseMatchCard = ({ match, index, showSimilarityScore, onSelect }) => (
  <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-3">
    <div className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
      <div className="min-w-0">
        <div className="break-words text-sm font-semibold text-gray-900 sm:truncate">
          <span>{index + 1}. </span>
          <SourceDocumentName match={match} />
        </div>
        <p className="text-xs text-gray-500">
          {formatPageRange(match.page_start, match.page_end)}
          {match.clause_type ? ` • ${formatClauseType(match.clause_type)}` : ''}
        </p>
      </div>
      {showSimilarityScore && (
        <div className="self-start rounded-md border border-gray-200 bg-white px-2 py-1 text-left sm:shrink-0 sm:text-right">
          <p className="text-[10px] uppercase tracking-wide text-gray-500">Similarity</p>
          <p className="text-xs font-semibold text-gray-900">{formatScore(match.scores?.combined)}</p>
        </div>
      )}
    </div>
    <button
      type="button"
      onClick={() => onSelect(match)}
      className="mt-2 w-full break-words whitespace-pre-wrap text-left border-l-4 border-sylvan-300 pl-3 text-sm text-gray-700 leading-6 hover:bg-white rounded-r-md transition-colors"
    >
      {match.clause_excerpt}
    </button>
    <div className="mt-1 flex flex-wrap items-center gap-2 pl-3 text-xs font-medium">
      <button
        type="button"
        onClick={() => onSelect(match)}
        className="text-sylvan-600 hover:text-sylvan-700 hover:underline"
      >
        Click to view full clause
      </button>
      <SourceDocumentButton match={match} />
    </div>
    {(match.prior_finding_summary || match.prior_suggested_edit || match.prior_human_outcome) && (
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        {match.prior_finding_summary && <ContextPill label="Prior issue" value={match.prior_finding_summary} />}
        {match.prior_human_outcome && <ContextPill label="Human outcome" value={match.prior_human_outcome} />}
        {match.prior_suggested_edit && <ContextPill label="Suggested edit" value={match.prior_suggested_edit} />}
        {match.prior_fallback_position && <ContextPill label="Fallback" value={match.prior_fallback_position} />}
      </div>
    )}
  </div>
);

const SourceDocumentButton = ({ match }) => {
  const href = sourceDocumentHref(match);
  if (!href) return null;
  return (
    <>
      <span className="text-gray-300">•</span>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-sylvan-600 hover:text-sylvan-700 hover:underline"
      >
        Open original contract
      </a>
    </>
  );
};

const SourceDocumentName = ({ match }) => {
  const filename = match.source_filename || 'Unknown source';
  const href = sourceDocumentHref(match);
  if (!href) {
    return <span>{filename}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-sylvan-700 hover:text-sylvan-800 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {filename}
    </a>
  );
};

const sourceDocumentHref = (match) => {
  if (!match?.source_document_url || !match?.contract_id) return null;
  return `/api/contract-qa/contracts/${encodeURIComponent(match.contract_id)}/source`;
};

const FullClauseModal = ({ match, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-3 sm:px-4" onClick={onClose}>
    <div
      className="max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {match.source_filename || 'Historical clause'}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {formatPageRange(match.page_start, match.page_end)}
            {match.clause_type ? ` • ${formatClauseType(match.clause_type)}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
      </div>
      <div className="max-h-[62vh] overflow-y-auto px-5 py-4">
        <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
          {match.full_clause_text || match.clause_excerpt}
        </p>
      </div>
    </div>
  </div>
);

const ContextPill = ({ label, value }) => (
  <div className="min-w-0 overflow-hidden rounded-md bg-white border border-gray-200 p-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 break-words text-xs leading-5 text-gray-700">{value}</p>
  </div>
);

const buildAssistantText = (payload) => {
  const count = payload.matches?.length || 0;
  if (payload.mode === 'clarification' || payload.mode === 'help' || payload.mode === 'list_clause_types') {
    return payload.assistant_message;
  }
  if (payload.mode === 'representative') {
    return `${payload.assistant_message} I found ${count} historical examples.`;
  }
  if (payload.mode === 'top_representative') {
    return `${payload.assistant_message} I found ${count} historical examples across ${payload.returned_categories?.length || 0} categories.`;
  }
  return `${payload.assistant_message} I found ${count} similar historical clauses.`;
};

const buildChatContext = (messages) => {
  const assistantPayloads = messages
    .filter((message) => message.role === 'assistant' && message.payload)
    .map((message) => message.payload);
  const previousPayload = assistantPayloads[assistantPayloads.length - 1];
  const excluded = new Map();
  assistantPayloads.forEach((payload) => {
    (payload.matches || []).forEach((match) => {
      if (match.contract_id && match.unit_id) {
        excluded.set(`${match.contract_id}:${match.unit_id}`, {
          contract_id: match.contract_id,
          unit_id: match.unit_id,
        });
      }
    });
  });
  return {
    previous_mode: previousPayload?.mode || null,
    previous_clause_type: previousPayload?.selected_clause_type || null,
    previous_query_text: previousPayload?.query_text || null,
    previous_top_categories: previousPayload?.returned_categories || [],
    excluded_matches: Array.from(excluded.values()),
  };
};

const latestClausePayload = (messages) =>
  [...messages]
    .reverse()
    .find((message) => message.role === 'assistant' && message.payload?.matches?.length > 0)?.payload || null;

const isClosestHistoricalFollowup = (message, previousPayload) => {
  if (!previousPayload?.matches?.length) return false;
  const text = message.toLowerCase();
  return (
    /\b(which|what)\b/.test(text) &&
    /\b(one|clause|example|result|these|them)\b/.test(text) &&
    /\b(closest|nearest|most similar|best match|top match|best|top)\b/.test(text)
  );
};

const closestHistoricalClauseMessage = (_message, previousPayload) => {
  const matches = [...(previousPayload.matches || [])];
  const best = matches.sort((left, right) => (right.scores?.combined || 0) - (left.scores?.combined || 0))[0];
  const originalIndex = (previousPayload.matches || []).findIndex(
    (match) => match.contract_id === best.contract_id && match.unit_id === best.unit_id
  );
  const score = formatScore(best.scores?.combined);
  return {
    id: `assistant-${Date.now()}`,
    role: 'assistant',
    text: `The closest clause from the results I just showed is #${originalIndex + 1}, from ${best.source_filename || 'Unknown source'}, based on the similarity score (${score}).`,
    payload: {
      ...previousPayload,
      matches: [best],
      assistant_message: 'Here is the closest clause from the previous results.',
      intent: {
        intent: 'find_similar_clause',
        confidence: 1,
        user_facing_summary: 'Rank previous historical clause results by similarity score.',
      },
    },
  };
};

const groupMatchesByClauseType = (matches) => {
  const groups = [];
  const groupByType = new Map();
  matches.forEach((match) => {
    const clauseType = match.clause_type || 'other';
    if (!groupByType.has(clauseType)) {
      const group = { clauseType, matches: [] };
      groupByType.set(clauseType, group);
      groups.push(group);
    }
    groupByType.get(clauseType).matches.push(match);
  });
  return groups;
};

const compactFindingTitle = (finding) => {
  if (finding.category) return sentenceCaseDisplay(finding.category);
  if (finding.clause_type) return formatClauseType(finding.clause_type);
  const words = String(finding.issue_summary || 'Risk finding')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  const title = words.length <= 6 ? words.join(' ') : `${words.slice(0, 6).join(' ')}...`;
  return sentenceCaseDisplay(title || 'Risk finding');
};

const sentenceCaseDisplay = (value) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const buildContractReviewAssistantBlurb = (findingsArtifact, fileName, agreementTypeWarning) => {
  const findings = findingsArtifact?.findings || [];
  const warning = agreementTypeWarning ? ` Contract type warning: ${agreementTypeWarning}` : '';
  return `${contractReviewSummaryText(findings, fileName)}${warning} What would you like to explore?`;
};

const contractReviewSummaryText = (findings, fileName, suffix = '') => {
  const highCount = findings.filter((finding) => finding.risk_level === 'high').length;
  const mediumCount = findings.filter((finding) => finding.risk_level === 'medium').length;
  const lowCount = findings.filter((finding) => finding.risk_level === 'low').length;
  const important = [...findings]
    .sort((left, right) => riskSortValue(left.risk_level) - riskSortValue(right.risk_level))
    .slice(0, 2)
    .map((finding) => finding.category || finding.issue_summary || finding.clause_type)
    .filter(Boolean);
  if (findings.length === 0) {
    return `I've reviewed ${shortContractName(fileName || 'the uploaded contract')}. No accepted risk findings were generated.${suffix}`;
  }
  const importantPhrase = important.length > 0 ? ` The most important issues are ${important.join(' and ')}.` : '';
  return `I've reviewed ${shortContractName(fileName || 'the uploaded contract')}. I found ${findings.length} risk finding${findings.length === 1 ? '' : 's'}: ${highCount} high, ${mediumCount} medium, and ${lowCount} low.${importantPhrase}${suffix}`;
};

const riskSortValue = (riskLevel) => {
  if (riskLevel === 'high') return 0;
  if (riskLevel === 'medium') return 1;
  if (riskLevel === 'low') return 2;
  return 3;
};

const COMPARISON_SUMMARY_TERMS = [
  { label: 'Contract Price', keywords: ['contract price', 'price', 'contract amount', 'amount', 'sum', 'compensation'] },
  { label: 'Governing Law', keywords: ['governing law', 'jurisdiction', 'venue', 'law'] },
  { label: 'Contract Duration', keywords: ['contract duration', 'duration', 'term', 'renewal'] },
  { label: 'Liability Cap', keywords: ['liability cap', 'limitation of liability', 'liability', 'cap'] },
  { label: 'IP Ownership', keywords: ['ip ownership', 'intellectual property', 'ownership', 'work product'] },
  { label: 'Payment Terms', keywords: ['payment terms', 'payment', 'invoice', 'net', 'retainage'] },
  { label: 'Termination Notice', keywords: ['termination notice', 'termination', 'notice to terminate'] },
  { label: 'Non-Compete', keywords: ['non-compete', 'non compete', 'restrictive covenant', 'competition'] },
  { label: 'Dispute Resolution', keywords: ['dispute resolution', 'dispute', 'arbitration', 'mediation', 'litigation'] },
];

const buildComparisonAssistantBlurb = (summary, baseName, revisedName) => {
  const differences = summary?.differences || [];
  const categories = mostSignificantComparisonTerms(differences).slice(0, 2);
  const clauseCount = summary?.metadata?.clauses_reviewed || summary?.clauses_reviewed || differences.length;
  const riskPhrase = categories.length > 0 ? ` The most significant risks are in ${categories.join(' and ')}.` : '';
  return `I've analyzed both contracts. Contract A (${shortContractName(baseName || 'Contract A')}) and Contract B (${shortContractName(revisedName || 'Contract B')}) have ${differences.length} difference${differences.length === 1 ? '' : 's'} across ${clauseCount} clause${clauseCount === 1 ? '' : 's'} reviewed.${riskPhrase} What would you like to explore?`;
};

const mostSignificantComparisonTerms = (differences) => {
  const riskOrder = { high: 0, medium: 1, low: 2 };
  return [...differences]
    .sort((left, right) => (riskOrder[comparisonRiskLevel(left)] ?? 3) - (riskOrder[comparisonRiskLevel(right)] ?? 3))
    .map((difference) => comparisonTerm(difference))
    .filter(Boolean);
};

const comparisonSummaryRows = (differences) =>
  COMPARISON_SUMMARY_TERMS.map((term) => {
    const difference = findDifferenceForSummaryTerm(differences, term);
    return {
      id: term.label,
      term: term.label,
      contractA: difference
        ? conciseComparisonText(difference.base_summary || difference.base_quote || difference.base_text || difference.removed_text)
        : 'No material difference identified',
      contractB: difference
        ? conciseComparisonText(difference.revised_summary || difference.revised_quote || difference.revised_text || difference.added_text)
        : 'No material difference identified',
      risk: difference ? comparisonRiskLevel(difference) : 'low',
    };
  });

const findDifferenceForSummaryTerm = (differences, term) => {
  const scored = differences
    .map((difference) => ({ difference, score: comparisonTermMatchScore(difference, term) }))
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score);
  return scored[0]?.difference || null;
};

const comparisonTermMatchScore = (difference, term) => {
  const haystack = [
    difference.term,
    difference.category,
    difference.clause_type,
    difference.title,
    difference.base?.title,
    difference.revised?.title,
    difference.practical_impact,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return term.keywords.reduce((score, keyword) => (haystack.includes(keyword) ? score + keyword.length : score), 0);
};

const comparisonTerm = (difference) => {
  const raw = difference.term || difference.category || difference.clause_type || difference.title || difference.difference_id || 'Contract Term';
  return formatComparisonTerm(raw);
};

const comparisonRiskLevel = (difference) => {
  const raw = String(difference.risk_level || difference.risk || difference.severity || '').toLowerCase();
  if (raw.includes('high')) return 'high';
  if (raw.includes('med')) return 'medium';
  if (raw.includes('low')) return 'low';
  const impact = String(difference.practical_impact || difference.summary || '').toLowerCase();
  if (impact.includes('material') || impact.includes('significant') || impact.includes('major')) return 'high';
  return 'medium';
};

const conciseComparisonText = (value) => {
  const text = String(value || 'Not specified').replace(/\s+/g, ' ').trim();
  if (text.length <= 90) return text;
  const sentenceEnd = ['. ', '! ', '? ']
    .map((marker) => text.indexOf(marker))
    .filter((index) => index > 0)
    .sort((left, right) => left - right)[0];
  const sentence = sentenceEnd ? text.slice(0, sentenceEnd + 1) : '';
  const candidate = sentence && sentence.length <= 100 ? sentence : text.slice(0, 87).trim();
  return `${candidate.replace(/[,:;\s]+$/, '')}...`;
};

const formatComparisonTerm = (value) => {
  const text = String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return 'Contract Term';
  return text
    .split(/([/ ])/)
    .map((part) => (part === '/' || part === ' ' ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
    .join('');
};

const shortContractName = (value) => {
  const text = String(value || '').replace(/\.(pdf|docx)$/i, '').trim();
  if (!text) return 'Contract';
  return text.length > 28 ? `${text.slice(0, 25).trim()}...` : text;
};

const formatChangeType = (value) => {
  if (!value) return 'Modified';
  return String(value)
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
};

const RiskBadge = ({ risk }) => {
  const normalized = risk || 'medium';
  const label = normalized === 'high' ? 'High Risk' : normalized === 'low' ? 'Low Risk' : 'Med Risk';
  const className = normalized === 'high'
    ? 'border-rose-200 bg-rose-50 text-rose-600'
    : normalized === 'low'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-amber-200 bg-amber-50 text-amber-700';
  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${className}`}>{label}</span>;
};

const formatIntent = (intent) => {
  if (intent.intent === 'browse_clause_type') return `Browse ${formatClauseType(intent.clause_type)} examples`;
  if (intent.intent === 'browse_clause_types') return 'Browse multiple clause types';
  if (intent.intent === 'find_similar_clause') return 'Find clauses similar to pasted language';
  if (intent.intent === 'greeting') return 'Greeting';
  if (intent.intent === 'gratitude') return 'Gratitude';
  if (intent.intent === 'farewell') return 'Farewell';
  if (intent.intent === 'help') return 'Help';
  if (intent.intent === 'list_clause_types') return 'List supported clause types';
  if (intent.intent === 'top_clause_examples') return 'Browse top clause examples';
  if (intent.intent === 'more_clause_examples') return 'Show more examples';
  return 'Clarify request';
};

const formatClauseType = (value) => {
  if (!value) return '';
  return value
    .split(/([/ ])/)
    .map((part) => (part === '/' || part === ' ' ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('');
};

const formatRiskLevel = (value) => {
  if (!value) return 'Risk';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const riskBadgeClass = (value) => {
  if (value === 'high') return 'bg-red-100 text-red-700';
  if (value === 'medium') return 'bg-amber-100 text-amber-700';
  if (value === 'low') return 'bg-green-100 text-green-700';
  return 'bg-gray-100 text-gray-600';
};

const formatScore = (value) => {
  if (typeof value !== 'number') return '0.00';
  return value.toFixed(2);
};

const formatPercent = (value) => {
  if (typeof value !== 'number') return '0%';
  return `${Math.round(value * 100)}%`;
};

const formatPageRange = (pageStart, pageEnd) => {
  if (!pageStart) return '';
  if (!pageEnd || pageStart === pageEnd) return `Page ${pageStart}`;
  return `Pages ${pageStart}-${pageEnd}`;
};

export const normalizeNdaReview = (review = {}) => ({
  ...review,
  expectedInformationFlow: review.expectedInformationFlow || review.expected_information_flow,
  ruleCatalog: (review.ruleCatalog || review.rule_catalog || []).map((rule) => ({
    id: rule.id,
    title: rule.title,
    category: rule.category,
  })),
  documentAssessment: {
    ...(review.documentAssessment || review.document_assessment || {}),
    isNda: review.documentAssessment?.isNda
      ?? review.documentAssessment?.is_nda
      ?? review.document_assessment?.is_nda,
    inferredInformationFlow: review.documentAssessment?.inferredInformationFlow
      || review.documentAssessment?.inferred_information_flow
      || review.document_assessment?.inferred_information_flow,
    mixedBusinessTerms: review.documentAssessment?.mixedBusinessTerms
      ?? review.documentAssessment?.mixed_business_terms
      ?? review.document_assessment?.mixed_business_terms,
    derivedCounterparty: review.documentAssessment?.derivedCounterparty
      || review.documentAssessment?.derived_counterparty
      || review.document_assessment?.derived_counterparty,
    counterpartyConfidence: review.documentAssessment?.counterpartyConfidence
      ?? review.documentAssessment?.counterparty_confidence
      ?? review.document_assessment?.counterparty_confidence,
    counterpartyEvidence: (
      review.documentAssessment?.counterpartyEvidence
      || review.documentAssessment?.counterparty_evidence
      || review.document_assessment?.counterparty_evidence
      || []
    ).map(normalizeNdaEvidence),
    documentStatedPurpose: review.documentAssessment?.documentStatedPurpose
      || review.documentAssessment?.document_stated_purpose
      || review.document_assessment?.document_stated_purpose,
    purposeConfidence: review.documentAssessment?.purposeConfidence
      ?? review.documentAssessment?.purpose_confidence
      ?? review.document_assessment?.purpose_confidence,
    purposeEvidence: (
      review.documentAssessment?.purposeEvidence
      || review.documentAssessment?.purpose_evidence
      || review.document_assessment?.purpose_evidence
      || []
    ).map(normalizeNdaEvidence),
    inferredScope: review.documentAssessment?.inferredScope
      || review.documentAssessment?.inferred_scope
      || review.document_assessment?.inferred_scope,
    scopeConfidence: review.documentAssessment?.scopeConfidence
      ?? review.documentAssessment?.scope_confidence
      ?? review.document_assessment?.scope_confidence,
    scopeRationale: review.documentAssessment?.scopeRationale
      || review.documentAssessment?.scope_rationale
      || review.document_assessment?.scope_rationale,
  },
  appliedScope: review.appliedScope || review.applied_scope || null,
  unusualFillIns: (review.unusualFillIns || review.unusual_fill_ins || []).map((field) => ({
    label: field.label,
    pageNumber: field.pageNumber ?? field.page_number,
    editId: field.editId || field.edit_id,
    location: field.location || '',
    line: field.line || '',
    after: field.after || null,
  })),
  intakeValidation: normalizeNdaIntakeValidation(review.intakeValidation || review.intake_validation || {}),
  ruleResults: (review.ruleResults || review.rule_results || []).map(normalizeNdaRuleResult),
  proposedEdits: (review.proposedEdits || review.proposed_edits || []).map(normalizeNdaEdit),
  escalationTriggers: review.escalationTriggers || review.escalation_triggers || [],
  uncertainties: review.uncertainties || [],
  warnings: (review.warnings || [])
    .filter((warning) => !isInternalNdaProcessingNotice(warning))
    .map(humanizeNdaMasterReference),
});

const isInternalNdaProcessingNotice = (message = '') => {
  const normalized = String(message).toLowerCase().replace(/\s+/g, ' ').trim();
  const hasInternalContext = normalized.includes('canonicalblocks')
    || normalized.includes('canonical blocks')
    || /\bp\d+-b\d+\b/.test(normalized)
    || /\bru-\d+\b/.test(normalized);
  const statesNoReviewImpact = [
    'all substantive text has been located',
    'no legal content appears to be missing',
    'no substantive content is missing',
    'does not affect the legal review',
    'does not affect substantive review',
  ].some((phrase) => normalized.includes(phrase));
  return hasInternalContext && statesNoReviewImpact;
};

const humanizeNdaMasterReference = (message = '') => String(message).replace(
  /\b(?:the\s+)?(?:approved\s+)?NDA-CLAUSE-[A-Z0-9-]+\s+clause\b/gi,
  'the Sylvan master NDA'
);

const normalizeNdaIntakeValidation = (intake = {}) => ({
  ...intake,
  expectedInformationFlow: intake.expectedInformationFlow || intake.expected_information_flow,
  inferredInformationFlow: intake.inferredInformationFlow || intake.inferred_information_flow,
  conflicts: intake.conflicts || [],
});

const normalizeNdaEvidence = (evidence = {}) => ({
  ...evidence,
  exactQuote: evidence.exactQuote || evidence.exact_quote,
  pageNumbers: evidence.pageNumbers || evidence.page_numbers || [],
  blockIds: evidence.blockIds || evidence.block_ids || [],
  reviewUnitIds: evidence.reviewUnitIds || evidence.review_unit_ids || [],
});

const normalizeNdaRuleResult = (result = {}) => ({
  ...result,
  ruleId: result.ruleId || result.rule_id,
  proposedEditIds: result.proposedEditIds || result.proposed_edit_ids || [],
  evidence: (result.evidence || []).map(normalizeNdaEvidence),
  absenceCheck: result.absenceCheck || result.absence_check
    ? normalizeNdaAbsenceCheck(result.absenceCheck || result.absence_check)
    : null,
});

const normalizeNdaAbsenceCheck = (absence = {}) => ({
  ...absence,
  completeDocumentReviewed: absence.completeDocumentReviewed ?? absence.complete_document_reviewed,
  inspectedReviewUnitIds: absence.inspectedReviewUnitIds || absence.inspected_review_unit_ids || [],
  searchTerms: absence.searchTerms || absence.search_terms || [],
});

const normalizeNdaEdit = (edit = {}) => {
  const anchor = edit.anchor || {};
  return {
    ...edit,
    editId: edit.editId || edit.edit_id,
    authorizationRuleId: edit.authorizationRuleId || edit.authorization_rule_id,
    clauseId: edit.clauseId || edit.clause_id,
    approvedText: edit.approvedText ?? edit.approved_text ?? null,
    anchor: {
      ...anchor,
      exactAnchorQuote: anchor.exactAnchorQuote || anchor.exact_anchor_quote,
      pageNumber: anchor.pageNumber || anchor.page_number,
      blockIds: anchor.blockIds || anchor.block_ids || [],
      reviewUnitId: anchor.reviewUnitId || anchor.review_unit_id,
    },
  };
};

export const ndaDispositionDisplay = (disposition) => {
  if (disposition === 'eligible_to_sign_under_guide') {
    return {
      label: 'Eligible to Sign Under Guide',
      guidance: 'The validated review found no required edits, Legal triggers, or unresolved uncertainties.',
      containerClass: 'border-emerald-200 bg-emerald-50 text-emerald-950',
      iconClass: 'bg-white text-emerald-700',
    };
  }
  if (disposition === 'edit_and_recirculate') {
    return {
      label: 'Edit and Recirculate',
      guidance: 'Apply the validated routine corrections and recirculate the NDA before signing.',
      containerClass: 'border-amber-300 bg-amber-50 text-amber-950',
      iconClass: 'bg-white text-amber-700',
    };
  }
  return {
    label: 'Escalate to Legal',
    guidance: 'Do not sign or send proposed changes without Legal review.',
    containerClass: 'border-rose-300 bg-rose-50 text-rose-950',
    iconClass: 'bg-white text-rose-700',
  };
};

const ndaRuleStatusDisplay = (result) => {
  if (result.consequence === 'escalate_to_legal' || result.status === 'uncertain') {
    return { label: 'Legal review', className: 'bg-rose-50 text-rose-700' };
  }
  if (result.consequence === 'routine_edit') {
    return { label: 'Edit required', className: 'bg-amber-50 text-amber-700' };
  }
  if (result.status === 'not_applicable' || result.status === 'not_found') {
    return { label: result.status === 'not_found' ? 'Not found' : 'Not applicable', className: 'bg-gray-100 text-gray-600' };
  }
  return { label: 'Pass', className: 'bg-emerald-50 text-emerald-700' };
};

const formatNdaOperation = (operation) => {
  const labels = {
    insert: 'Insert approved language',
    replace: 'Replace with approved language',
    delete: 'Delete language',
    manual_fill: 'Complete administrative field',
  };
  return labels[operation] || String(operation || 'Routine correction').replace(/_/g, ' ');
};

export const formatNdaPages = (pages = []) => {
  const values = [...new Set(pages)].filter(Boolean).sort((left, right) => left - right);
  if (!values.length) return 'Source location';
  if (values.length === 1) return `Page ${values[0]}`;
  return `Pages ${values.join(', ')}`;
};

export const formatNdaJobStatus = (job) => {
  if (!job) return '';
  if (job.status === 'succeeded' || job.stage === 'completed') return 'Complete';
  if (job.status === 'failed') return 'Failed';
  const labels = {
    original_retention: 'Document Retention',
    gemini_extraction: 'AI Extraction',
    review_units: 'Clause Organization',
    standard_alignment: 'Standard Alignment',
    primary_segments: 'AI Policy Review',
    primary_evaluation: 'AI Policy Review',
    semantic_repair: 'Evidence Validation',
    verification: 'Independent Verification',
    validation_and_routing: 'Validating Results',
    artifacts: 'Preparing Dashboard',
    queued: 'Queued',
    running: 'Processing',
  };
  return labels[job.stage] || labels[job.status] || 'Processing';
};

export const ndaProgress = (job) => {
  if (!job) return 0;
  if (job.status === 'succeeded' || job.stage === 'completed') return 100;
  const progress = {
    original_retention: 8,
    queued: 0,
    gemini_extraction: 20,
    review_units: 35,
    standard_alignment: 45,
    primary_segments: 55,
    primary_evaluation: 65,
    semantic_repair: 72,
    verification: 82,
    validation_and_routing: 92,
    artifacts: 97,
  };
  if (job.status === 'queued' || !job.stage) return 0;
  return progress[job.stage] ?? (job.status === 'running' ? 12 : 0);
};

const formatJobStatus = (job) => {
  if (!job) return '';
  if (job.status === 'succeeded') return 'Ready';
  if (job.status === 'failed') return 'Failed';
  if (job.stage === 'gemini_extraction' || job.stage === 'extract_base' || job.stage === 'extract_revised') return 'AI Extraction';
  if (job.stage === 'claude_review' || job.stage === 'compare') return 'AI Review';
  if (job.stage) return `Status: ${job.stage.replace(/_/g, ' ')}`;
  return `Status: ${job.status}`;
};

const comparisonProgress = (job) => {
  if (!job) return 5;
  if (job.status === 'succeeded' || job.stage === 'completed') return 100;
  if (job.status === 'failed') return Math.max(stageProgress(job.stage), 10);
  return stageProgress(job.stage || job.status);
};

const stageProgress = (stage) => {
  const normalized = String(stage || '').toLowerCase();
  if (normalized.includes('completed')) return 100;
  if (normalized.includes('memory_import')) return 92;
  if (normalized.includes('claude_review')) return 78;
  if (normalized.includes('clause_classification')) return 62;
  if (normalized.includes('agreement_type_assessment')) return 52;
  if (normalized.includes('review_units')) return 45;
  if (normalized.includes('gemini_extraction')) return 25;
  if (normalized.includes('extract_base')) return 25;
  if (normalized.includes('extract_revised')) return 50;
  if (normalized.includes('compare')) return 80;
  if (normalized.includes('running')) return 15;
  if (normalized.includes('queued')) return 5;
  return 10;
};

const looksLikeClauseLibraryRequest = (message) => {
  const text = message.toLowerCase();
  return [
    'example',
    'similar to this',
    'simialr',
    'clauses like this',
    'clause like this',
    'historical',
    'clause type',
    'clause types',
    'show me',
    'give me',
    'find clauses',
    'find clause',
  ].some((phrase) => text.includes(phrase));
};

const looksLikeUploadedContractRiskQuestion = (message) => {
  const text = message.toLowerCase();
  return /\b(risk|risks|finding|findings|issue|issues|problem|problems|red flag|red flags)\b/.test(text);
};

const looksLikeUploadedContractSimilarityRequest = (message) => {
  const text = message.toLowerCase();
  const asksForSimilarity = /\b(similar|simialr|like|closest|nearest)\b/.test(text);
  const referencesUploadedContract = [
    'this contract',
    'the contract',
    'uploaded contract',
    'current contract',
  ].some((phrase) => text.includes(phrase));
  const providesPastedClause = /\b(this|below)\s*:/i.test(message) && message.split(':').slice(1).join(':').trim().length > 20;
  return asksForSimilarity && referencesUploadedContract && !providesPastedClause;
};

const looksLikeComparisonSimilarityRequest = (message) => {
  const text = message.toLowerCase();
  const asksForSimilarity = /\b(similar|simialr|like|closest|nearest)\b/.test(text);
  const referencesComparisonContract = [
    'this contract',
    'these contracts',
    'the contract',
    'comparison',
    'uploaded',
    'base contract',
    'revised contract',
    'current contract',
    'other contract',
  ].some((phrase) => text.includes(phrase));
  const providesPastedClause = /\b(this|below)\s*:/i.test(message) && message.split(':').slice(1).join(':').trim().length > 20;
  return asksForSimilarity && referencesComparisonContract && !providesPastedClause;
};

const contractHistory = (messages) =>
  messages
    .filter((message) => message.role === 'user' || message.cited_unit_ids?.length || message.cited_finding_ids?.length)
    .slice(-8)
    .map((message) => ({
      role: message.role,
      text: message.text,
      cited_unit_ids: message.cited_unit_ids || [],
      cited_finding_ids: message.cited_finding_ids || [],
    }));

const comparisonHistory = (messages) =>
  messages
    .filter((message) => message.role === 'user' || message.cited_difference_ids?.length)
    .slice(-8)
    .map((message) => ({
      role: message.role,
      text: message.text,
      cited_difference_ids: message.cited_difference_ids || [],
    }));

const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export default ContractQAAgent;
